import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { trackSignupSlackConnected, trackSignupProfileCompleted } from '../lib/analytics';
import { SignupShowcase } from '../components/SignupShowcase';
import { DarkGreenTexture } from '../components/DarkGreenTexture';
import { WaitlistModal } from '../components/WaitlistModal';
import { BillingModal } from '../components/BillingModal';
import { Check, ChevronDown, Shield, Lock, FileText, ArrowRight, ArrowLeft, Info, X, Search, Hash, MessageSquare, Headphones, Users, Grid3x3, Bell, Star, CreditCard, Calendar, ShieldCheck, Loader2, ArrowLeftRight, Plug, UserPlus, Sparkles, CheckCircle, ChevronRight, LayoutGrid, Plus, Send, PanelLeft, Folder, SquarePen, Zap, Brain, MoreHorizontal, Gauge, Settings2, PhoneCall, Paperclip, Home, GraduationCap, Activity, FolderOpen, Database, Globe, Monitor, Network, UserCircle, Copy } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../data/workspaceTracks';
import { ONBOARDING_INTERVIEW, ONBOARDING_OPENER, ONBOARDING_SUMMARY, stepChoices, INTERVIEW_ROLES, type InterviewRole } from '../data/onboardingInterview';
import { WorkspaceViews, WorkspaceViewId } from '../components/gigachat/WorkspaceViews';
const ALL_TOOLS = [{
  id: 'slack',
  name: 'Slack',
  icon: 'slack',
  category: 'communication'
}, {
  id: 'gmail',
  name: 'Gmail',
  icon: 'gmail',
  category: 'communication'
}, {
  id: 'outlook',
  name: 'Outlook',
  icon: 'microsoftoutlook',
  category: 'communication'
}, {
  id: 'teams',
  name: 'Microsoft Teams',
  icon: 'microsoftteams',
  category: 'communication'
}, {
  id: 'discord',
  name: 'Discord',
  icon: 'discord',
  category: 'communication'
}, {
  id: 'googledrive',
  name: 'Google Drive',
  icon: 'googledrive',
  category: 'docs'
}, {
  id: 'googlecalendar',
  name: 'Google Calendar',
  icon: 'googlecalendar',
  category: 'docs'
}, {
  id: 'notion',
  name: 'Notion',
  icon: 'notion',
  category: 'docs'
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: 'confluence',
  category: 'docs'
}, {
  id: 'coda',
  name: 'Coda',
  icon: 'coda',
  category: 'docs'
}, {
  id: 'dropbox',
  name: 'Dropbox',
  icon: 'dropbox',
  category: 'docs'
}, {
  id: 'github',
  name: 'GitHub',
  icon: 'github',
  category: 'engineering'
}, {
  id: 'gitlab',
  name: 'GitLab',
  icon: 'gitlab',
  category: 'engineering'
}, {
  id: 'bitbucket',
  name: 'Bitbucket',
  icon: 'bitbucket',
  category: 'engineering'
}, {
  id: 'sentry',
  name: 'Sentry',
  icon: 'sentry',
  category: 'engineering'
}, {
  id: 'datadog',
  name: 'Datadog',
  icon: 'datadog',
  category: 'engineering'
}, {
  id: 'linear',
  name: 'Linear',
  icon: 'linear',
  category: 'project'
}, {
  id: 'jira',
  name: 'Jira',
  icon: 'jira',
  category: 'project'
}, {
  id: 'asana',
  name: 'Asana',
  icon: 'asana',
  category: 'project'
}, {
  id: 'trello',
  name: 'Trello',
  icon: 'trello',
  category: 'project'
}, {
  id: 'monday',
  name: 'Monday.com',
  icon: 'mondaydotcom',
  category: 'project'
}, {
  id: 'salesforce',
  name: 'Salesforce',
  icon: 'salesforce',
  category: 'crm'
}, {
  id: 'hubspot',
  name: 'HubSpot',
  icon: 'hubspot',
  category: 'crm'
}, {
  id: 'pipedrive',
  name: 'Pipedrive',
  icon: 'pipedrive',
  category: 'crm'
}, {
  id: 'zendesk',
  name: 'Zendesk',
  icon: 'zendesk',
  category: 'support'
}, {
  id: 'intercom',
  name: 'Intercom',
  icon: 'intercom',
  category: 'support'
}, {
  id: 'front',
  name: 'Front',
  icon: 'front',
  category: 'support'
}, {
  id: 'stripe',
  name: 'Stripe',
  icon: 'stripe',
  category: 'finance'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  icon: 'quickbooks',
  category: 'finance'
}, {
  id: 'figma',
  name: 'Figma',
  icon: 'figma',
  category: 'design'
}, {
  id: 'miro',
  name: 'Miro',
  icon: 'miro',
  category: 'design'
}, {
  id: 'googleanalytics',
  name: 'Google Analytics',
  icon: 'googleanalytics',
  category: 'analytics'
}, {
  id: 'mixpanel',
  name: 'Mixpanel',
  icon: 'mixpanel',
  category: 'analytics'
}, {
  id: 'amplitude',
  name: 'Amplitude',
  icon: 'amplitude',
  category: 'analytics'
}, {
  id: 'mailchimp',
  name: 'Mailchimp',
  icon: 'mailchimp',
  category: 'marketing'
}, {
  id: 'googleads',
  name: 'Google Ads',
  icon: 'googleads',
  category: 'marketing'
}, {
  id: 'hootsuite',
  name: 'Hootsuite',
  icon: 'hootsuite',
  category: 'marketing'
}];
function getScopesForTool(tool: any) {
  if (tool.id === 'slack') {
    return [{
      id: 'read_channels',
      label: 'Read channel messages',
      description: "View messages in public channels you're a member of.",
      required: true
    }, {
      id: 'search_history',
      label: 'Search history',
      description: "Search across messages so Giga can cite what's been said.",
      required: true
    }, {
      id: 'read_files',
      label: 'Read files & attachments',
      description: 'Read files shared in channels and DMs.',
      required: false
    }, {
      id: 'read_dms',
      label: 'Read your direct messages',
      description: 'Index your DMs so Giga can find specific conversations.',
      required: false
    }, {
      id: 'post',
      label: 'Post on your behalf',
      description: 'Reply in channels and send DMs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'docs') {
    return [{
      id: 'read_files',
      label: 'Read your files & folders',
      description: `Index docs, sheets, slides and PDFs in your ${tool.name}.`,
      required: true
    }, {
      id: 'read_shared',
      label: 'Read shared drives',
      description: 'Include drives shared with you by your team.',
      required: true
    }, {
      id: 'write_docs',
      label: 'Create & edit documents',
      description: 'Draft new docs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'communication') {
    return [{
      id: 'read_msgs',
      label: 'Read messages & channels',
      description: 'Index conversations you have access to.',
      required: true
    }, {
      id: 'send_msgs',
      label: 'Send messages',
      description: 'Post updates and replies on your behalf.',
      required: true
    }];
  }
  if (tool.category === 'engineering' || tool.category === 'project') {
    return [{
      id: 'read_issues',
      label: 'Read issues & projects',
      description: 'Index tickets, PRs, and project states.',
      required: true
    }, {
      id: 'write_issues',
      label: 'Update issues',
      description: 'Change statuses and add comments.',
      required: false
    }];
  }
  return [{
    id: 'read_data',
    label: `Read ${tool.name} data`,
    description: 'Index records and objects you have access to.',
    required: true
  }, {
    id: 'write_data',
    label: `Update ${tool.name} records`,
    description: 'Create and modify records on your behalf.',
    required: false
  }];
}
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11102" data-mp="l9afl3d1496c">
      
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-11103" data-mp="7i5d7tia12sb" />
      
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-11104" data-mp="2kvnsduw0r4h" />
      
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-11105" data-mp="1iyvk7euixymz" />
      
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-11106" data-mp="5rr1ay0trbc1" />
      
    </svg>;
}
function GoogleIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-11107" data-mp="3j52qr396b213">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z" fill="#4285F4" data-id="element-11108" data-mp="3e5ptrpol8eks" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853" data-id="element-11109" data-mp="399ixkolzuhg1" />
      <path d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" fill="#FBBC05" data-id="element-11110" data-mp="17h4j5zinrv4d" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335" data-id="element-11111" data-mp="1fwahldu8cuhq" />
    </svg>;
}

// The three Google Workspace tools whose scopes are requested inside the same
// Google consent, so a Google signup arrives with a useful brain already
// connected instead of an empty one.
const GOOGLE_WORKSPACE_TOOLS = ['gmail', 'googledrive', 'googlecalendar'];
const BUSINESS_TYPES = ['E-commerce / DTC', 'Consumer app', 'SaaS / B2B software', 'Marketplace', 'Fintech', 'Healthcare / health-tech', 'Education / ed-tech', 'Media & entertainment', 'Gaming', 'AI / ML', 'Hardware / IoT', 'Real estate / proptech', 'Travel & hospitality', 'Food & beverage', 'Retail (brick & mortar)', 'Logistics & supply chain', 'Professional services', 'Agency / consulting', 'Nonprofit', 'Government / public sector', 'Manufacturing', 'Energy & climate', 'Crypto / web3', 'Other'];

// The connected Slack workspace. In production this comes from the Slack OAuth
// handshake (team name) so the post-connect screens address the user by their
// real workspace instead of a placeholder. Mirrors the account the user
// "connects" earlier in the flow (trackrheadquarters.slack.com).
const WORKSPACE_NAME = 'Trackr HQ';
const WORKSPACE_INITIAL = WORKSPACE_NAME.charAt(0);
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  ariaLabel,
  searchable = true
}: {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Focus the search field when the panel opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);
  return <div ref={wrapperRef} className="relative" data-id="element-11112" data-mp="1436kwufyme38">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${value ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11113" data-mp="32gzunyodljbu">
        
        <span className="truncate" data-id="element-11114" data-mp="msb2nbil8sel">{value || placeholder}</span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11115" data-mp="mpolck5wayj6" />
        
      </button>

      <AnimatePresence data-id="element-11116" data-mp="1eip94mo9v2g8">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11117" data-mp="1ejgxpdjgbzx0">
          
            {searchable && <div className="p-2 border-b border-neutral-100" data-id="element-11118" data-mp="3q6x18yh2bob9">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 focus-within:border-[#EA580C] transition-colors" data-id="element-11119" data-mp="3v7ur7x8bdmnb">
                  <Search size={16} className="text-neutral-400 shrink-0" data-id="element-11120" data-mp="3ioz5dwrt0bqi" />
                  <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none" data-id="element-11121" data-mp="3iop9jsk2vacw" />
              
                </div>
              </div>}
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11122" data-mp="33xzz4sefxza3">
              {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-neutral-400" data-id="element-11123" data-mp="2yywsx4mq5n0j">
                  No matches found
                </li> : filtered.map(option => {
            const isSelected = option === value;
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11124" data-mp="38x2v7ncg0v5x">
                      <button type="button" onClick={() => {
                onChange(option);
                setOpen(false);
              }} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11125" data-mp="2l80f4jxgjadj">
                    
                        <span className="truncate" data-id="element-11126" data-mp="1siqkg3yvn8ga">{option}</span>
                        {isSelected && <Check size={16} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11127" data-mp="1xb1kr7xj4w9d" />}
                      </button>
                    </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = 'Select options'
}: {
  values: string[];
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  return <div ref={wrapperRef} className="relative" data-id="element-11128" data-mp="4lr3uv3yg5dd">
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${values.length > 0 ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11129" data-mp="4mk195h0xiiu">
        
        <span className="truncate" data-id="element-11130" data-mp="s9safyhkpbq5">
          {values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11131" data-mp="ja589ah0s1w1" />
        
      </button>

      <AnimatePresence data-id="element-11132" data-mp="3415o5fjsahc5">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11133" data-mp="3404ad3bdvgqq">
          
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11134" data-mp="30ioxd661hzw1">
              {options.map(option => {
            const isSelected = values.includes(option);
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11135" data-mp="2rijqqy8o75ix">
                    <button type="button" onClick={() => onChange(option)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5]' : 'hover:bg-neutral-50'}`} data-id="element-11136" data-mp="2rjuxn6xewte9">
                    
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-11137" data-mp="23sfpuymv9jvm">
                      
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-11138" data-mp="1p6in4axy7ah3" />}
                      </div>
                      <span className={`truncate ${isSelected ? 'text-[#333333] font-medium' : 'text-[#333333]'}`} data-id="element-11139" data-mp="1k9a3e5x5iydq">
                      
                        {option}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VisibilityDropdown({
  value,
  onChange
}: {
  value: 'team' | 'private';
  onChange: (val: 'team' | 'private') => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const isPrivate = value === 'private';
  const OPTIONS: {
    id: 'team' | 'private';
    Icon: typeof Users;
    label: string;
  }[] = [{
    id: 'team',
    Icon: Users,
    label: 'Team-only'
  }, {
    id: 'private',
    Icon: Lock,
    label: 'Private (invite only)'
  }];
  return <div ref={wrapperRef} className="relative" data-id="element-11140" data-mp="b19dkalvtypn">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Who can use this connection?" className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${open ? 'border-[#333333] bg-white' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} text-neutral-600`} data-id="element-11141" data-mp="3aja66wwfeu7p">
        
        {isPrivate ? <Lock size={11} data-id="element-11142" data-mp="2qtq8oilsh8ez" /> : <Users size={11} data-id="element-11143" data-mp="2qu82l8kzbajq" />}
        {isPrivate ? 'Private' : 'Team-only'}
        <ChevronDown size={12} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11144" data-mp="2vsjf5r2nrz5n" />
        
      </button>

      <AnimatePresence data-id="element-11145" data-mp="2o7z90hz4jsyz">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" role="listbox" data-id="element-11146" data-mp="2jah02rl3xdqz">
          
            <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11147" data-mp="20iefj55chdbg">
              Who should have access?
            </div>
            {OPTIONS.map(opt => {
          const active = opt.id === value;
          return <button key={opt.id} type="button" role="option" aria-selected={active} onClick={() => {
            onChange(opt.id);
            setOpen(false);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${active ? 'text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11148" data-mp="1mkzo5uvj6vgu">
                
                  <opt.Icon size={15} className="text-neutral-500 shrink-0" data-id="element-11149" data-mp="3tmwg0g6e7nqs" />
                  <span className="flex-1 truncate" data-id="element-11150" data-mp="3tozabcekw31b">{opt.label}</span>
                  {active && <Check size={15} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11151" data-mp="3onjrlqzvi8m3" />}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StickyRevealBar({
  children
}: {
  children: React.ReactNode;
}) {
  // A persistent fixed action bar pinned to the bottom of the screen. It stays
  // visible no matter where the user is in the scroll, with a one-time slide-up
  // entrance on mount. Self-contained so it never affects the page hook order.
  return <motion.div initial={{
    y: 90,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 34,
    delay: 0.1
  }} className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-11152" data-mp="30xfn7hev24u1">
      
      <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-11153" data-mp="2vtn1vjotjmp7">{children}</div>
    </motion.div>;
}
// The tool picker must show the real, familiar mark for every company — not
// a letter badge or a loosely drawn icon. Each entry maps directly to the
// product's domain; logo.dev serves the canonical brand asset and the brand
// favicon is a resilient fallback if that service is temporarily unavailable.
const TOOL_LOGO_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  microsoftoutlook: 'outlook.com',
  microsoftteams: 'teams.microsoft.com',
  discord: 'discord.com',
  googledrive: 'drive.google.com',
  notion: 'notion.so',
  confluence: 'atlassian.com',
  coda: 'coda.io',
  dropbox: 'dropbox.com',
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
  sentry: 'sentry.io',
  datadog: 'datadoghq.com',
  linear: 'linear.app',
  jira: 'atlassian.com',
  asana: 'asana.com',
  trello: 'trello.com',
  mondaydotcom: 'monday.com',
  salesforce: 'salesforce.com',
  hubspot: 'hubspot.com',
  pipedrive: 'pipedrive.com',
  zendesk: 'zendesk.com',
  intercom: 'intercom.com',
  front: 'front.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  miro: 'miro.com',
  googleanalytics: 'analytics.google.com',
  mixpanel: 'mixpanel.com',
  amplitude: 'amplitude.com',
  mailchimp: 'mailchimp.com',
  googleads: 'ads.google.com',
  hootsuite: 'hootsuite.com'
};
const LOGO_DEV_TOKEN = 'pk_E8dUI9PzTHmmaUwDeUCyiQ';
function ToolLogo({
  name,
  icon,
  className = 'w-12 h-12 mx-auto mb-3'
}: {
  name: string;
  icon: string;
  className?: string;
  fallbackTextSize?: string;
}) {
  const [source, setSource] = useState<'canonical' | 'favicon'>('canonical');
  const domain = TOOL_LOGO_DOMAINS[icon];
  const src = source === 'canonical' ? `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128` : `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return <img src={src} alt={name} onError={() => {
    if (source === 'canonical') setSource('favicon');
  }} className={`${className} object-contain`} data-id="element-11154" data-mp="2r8igscwzpjtf" />;
}
function TeamsIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11155" data-mp="35201i11ex2l1">
      
      <path d="M16.5 13.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5z" fill="#5059C9" data-id="element-11156" data-mp="3a0telscserko" />
      
      <path d="M22.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#5059C9" data-id="element-11157" data-mp="2w8n16e10qyox" />
      
      <path d="M7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z" fill="#7B83EB" data-id="element-11158" data-mp="309qfgw3f4mo1" />
      
      <path d="M13.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#7B83EB" data-id="element-11159" data-mp="3nzksbdnqmb8a" />
      
    </svg>;
}
function GigaHeader({
  hideOnMobile = false
}: {
  hideOnMobile?: boolean;
}) {
  return <div className={`border-b border-neutral-100 px-6 py-4 ${hideOnMobile ? 'hidden lg:block' : ''}`} data-id="element-11160" data-mp="250wirfofwkq8">
      
      <Link to="/" aria-label="Back to Giga home" className="inline-flex items-center gap-2 group w-fit" data-id="element-11161" data-mp="1v53ai8hr7de9">
        
        <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-11162" data-mp="1r3rjbyj8ek3y">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11163" data-mp="2nsfmjoqag2tb" />
          
        </div>
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-11164" data-mp="2a1asr39lq4qw" />
        
      </Link>
    </div>;
}
export function Signup() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const navigate = useNavigate();
  // Returning users go through /app-screen-mockups/sign-in, which is its own
  // screen — this component is only the signup flow.
  const isLogin = false;
  // The feature isn't live yet — the public "Get started" CTAs send users here
  // with ?waitlist=1, which drops them on the Slack connect screen (step 2)
  // with the waitlist popup open over it.
  const waitlistParam = new URLSearchParams(location.search).get('waitlist') === '1';
  // Any screen in this flow can be opened directly with ?step=<n> (e.g.
  // ?step=22 for the Giga web chat landing), so reviewing a single screen
  // doesn't mean clicking through the whole dummy signup.
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  // A few screens also get their own real route, so they show up in the route
  // dropdown and can be opened without knowing a step number.
  const STEP_ROUTES: Record<string, number> = {
    '/app-screen-mockups/google-consent': 20,
    '/app-screen-mockups/google-permissions': 21,
    '/app-screen-mockups/giga-chat': 22
  };
  const stepFromPath = STEP_ROUTES[location.pathname];
  const stepFromUrl = stepFromPath ?? (Number.isFinite(stepParam) && stepParam > 0 ? stepParam : undefined);
  const [step, setStep] = useState(stepFromUrl ?? screenInit.step ?? (waitlistParam ? 2 : isLogin ? 10 : 1));
  const [showWaitlist, setShowWaitlist] = useState(waitlistParam);
  // Which front door the user came through. Slack signups end in Slack; Google
  // signups have no Slack to hand off to, so they land in Giga's web chat and
  // can add Slack later. Only the final destination differs — the setup steps
  // in between are shared.
  const [authProvider, setAuthProvider] = useState<'slack' | 'google'>(stepFromUrl && stepFromUrl >= 20 ? 'google' : 'slack');
  // Step 22: the web chat's nav stays out of the way. A collapsed rail holds
  // the brand mark and one toggle; the sidebar itself slides in over the page
  // only when asked for, so the composer is never competing with navigation.
  const [chatNavOpen, setChatNavOpen] = useState(false);
  const [chatNavMoreOpen, setChatNavMoreOpen] = useState(false);
  const [chatAccountOpen, setChatAccountOpen] = useState(false);
  const [chatBillingOpen, setChatBillingOpen] = useState(false);
  // Step 22 composer: what this chat is allowed to draw on. Empty by default —
  // Giga uses everything the user can see unless they narrow it.
  const [contextOpen, setContextOpen] = useState(false);
  const [contextTab, setContextTab] = useState<'All' | 'Groups' | 'Tracks' | 'Credentials' | 'Databases'>('All');
  const [contextQuery, setContextQuery] = useState('');
  const [chatContext, setChatContext] = useState<string[]>([]);
  // Which pane the chat shell is showing. 'chat' is the thread; everything
  // else is a workspace view rendered inside the same shell, so the nav never
  // navigates away.
  const [chatView, setChatView] = useState<'chat' | WorkspaceViewId>('chat');
  // Picking a destination does NOT close the nav — once opened it stays until
  // the user closes it, so moving between views doesn't cost a re-open.
  const openChatView = (v: 'chat' | WorkspaceViewId) => {
    setChatView(v);
    setChatNavMoreOpen(false);
  };
  // DEBUG: a brand-new workspace with nothing connected yet, so the context
  // picker's empty state can be reviewed without unpicking the setup steps.
  const [chatNoConnections, setChatNoConnections] = useState(false);
  const [chatNotifOpen, setChatNotifOpen] = useState(false);
  const [chatChecklistOpen, setChatChecklistOpen] = useState(false);
  const [chatChecklistStep, setChatChecklistStep] = useState<string | null>('Connect your tools');
  // Step 22: the live thread. Empty until the first prompt, at which point the
  // greeting is replaced by the conversation and the composer stays put.
  type ChatMsg = {
    id: number;
    role: 'user' | 'giga';
    text?: string;
    steps?: {
      icon: string;
      label: string;
    }[];
    answer?: string[];
    sources?: {
      icon: string;
      label: string;
    }[];
    thinking?: boolean;
    /** Onboarding interview: suggested answers for this question. */
    choices?: string[];
    multi?: boolean;
    /** Set once the question has been answered, so the chips retire. */
    answered?: boolean;
    /** Closing summary of what the interview produced. */
    created?: readonly {
      icon: string;
      title: string;
      detail: string;
    }[];
    /** A Giga concept being introduced alongside the question. */
    concept?: {
      label: string;
      title: string;
      lines: string[];
    };
  };
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  // Conversational onboarding. `null` = never started; an index = mid-interview;
  // 'done' = finished, which is what ticks the setup checklist.
  const [interviewIndex, setInterviewIndex] = useState<number | 'done' | null>(null);
  const [interviewPicks, setInterviewPicks] = useState<string[]>([]);
  // Captured at step one; every later question branches on it.
  const [interviewRole, setInterviewRole] = useState<InterviewRole | null>(null);
  // Derived rather than hardcoded, so the ring and the list can never disagree.
  const setupProgress = (() => {
    const done = 2 + (interviewIndex === 'done' ? 1 : 0) + (chatMessages.length > 0 ? 1 : 0);
    return {
      done,
      total: 6,
      ratio: done / 6
    };
  })();
  const [chatCreditNoticeSeen, setChatCreditNoticeSeen] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const chatScripts: Record<string, {
    steps: {
      icon: string;
      label: string;
    }[];
    answer: string[];
    sources: {
      icon: string;
      label: string;
    }[];
  }> = {
    "What changed in my inbox while I was out?": {
      steps: [{
        icon: 'gmail',
        label: 'Read 63 unread threads in Gmail'
      }, {
        icon: 'googlecalendar',
        label: 'Cross-checked against your calendar'
      }],
      answer: ['**Three things need you today.**', '**Acme renewal** — their legal team sent redlines on Tuesday and asked for a reply by Friday. Two clauses changed: liability cap and data residency.', '**Northwind pilot** — Marcus confirmed the pilot start for 8 Sep and is waiting on your sign-off for seat count.', '**Invoice #2214** — finance flagged it as 21 days overdue and has already chased twice.', 'The remaining 60 threads are newsletters, calendar noise and CC traffic. I can archive those if you want.'],
      sources: [{
        icon: 'gmail',
        label: '63 threads'
      }, {
        icon: 'googlecalendar',
        label: '4 events'
      }]
    },
    'Summarise the deck in Drive from yesterday': {
      steps: [{
        icon: 'googledrive',
        label: 'Found “Q3 Board Review.pdf” — edited yesterday'
      }, {
        icon: 'googledrive',
        label: 'Read 24 slides'
      }],
      answer: ['**Q3 Board Review — 24 slides, edited by Priya at 18:40.**', 'The argument is that growth held while spend fell: revenue up 34% quarter over quarter, CAC down 12%, and net retention at 118%.', 'Two risks are named — hiring is a quarter behind plan, and one enterprise renewal worth 8% of ARR is unsigned.', 'Slide 19 has a gap: the ask is stated but there is no numbers slide behind it. Worth filling before Thursday.'],
      sources: [{
        icon: 'googledrive',
        label: 'Q3 Board Review.pdf'
      }]
    },
    "Who am I meeting tomorrow, and what do I need?": {
      steps: [{
        icon: 'googlecalendar',
        label: 'Read tomorrow’s 4 events'
      }, {
        icon: 'gmail',
        label: 'Pulled the last thread with each attendee'
      }],
      answer: ['**Four meetings tomorrow. Two need prep.**', '**09:30 — Acme, legal review (45m).** Bring a position on the liability cap; their redlines are still unanswered.', '**11:00 — Internal standup (15m).** Nothing needed.', '**14:00 — Northwind, pilot kickoff (60m).** Marcus wants seat count confirmed. Last thread says 25, your notes say 40.', '**16:30 — 1:1 with Priya (30m).** She flagged the slide 19 gap, so expect that.', 'I can draft the Acme reply and a one-pager for Northwind now.'],
      sources: [{
        icon: 'googlecalendar',
        label: '4 events'
      }, {
        icon: 'gmail',
        label: '6 threads'
      }]
    }
  };
  // ── Conversational onboarding ──────────────────────────────────────────
  // Giga asks one question per turn. Answers arrive either from the suggestion
  // chips or from the composer, so the interview never blocks free typing.
  const pushInterviewQuestion = (index: number, role: InterviewRole | null, delay = 900) => {
    const step = ONBOARDING_INTERVIEW[index];
    const choices = stepChoices(step, role);
    const id = Date.now() + index + 2;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: step.work
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...step.question],
        concept: step.concept ? {
          ...step.concept,
          lines: [...step.concept.lines]
        } : undefined,
        choices,
        multi: step.multi
      } : m));
      // Opt out, not in: the likely answers arrive already ticked.
      if (step.multi && step.preselectAll) setInterviewPicks(choices);
    }, delay);
  };
  const startOnboarding = () => {
    setChatChecklistOpen(false);
    setChatCreditNoticeSeen(true);
    setInterviewIndex(0);
    setInterviewPicks([]);
    setInterviewRole(null);
    setChatDraft('');
    // The composer is pre-populated and sent for them, so the interview starts
    // as a normal message in the thread rather than a modal takeover.
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      text: ONBOARDING_OPENER
    }]);
    pushInterviewQuestion(0, null, 1100);
  };
  const answerInterview = (text: string) => {
    if (typeof interviewIndex !== 'number') return;
    const next = interviewIndex + 1;
    // Step one sets the branch for every question after it. Free text still
    // works — anything unrecognised falls back to the generalist set.
    let role = interviewRole;
    if (ONBOARDING_INTERVIEW[interviewIndex].key === 'role') {
      role = INTERVIEW_ROLES.find(r => r.toLowerCase() === text.trim().toLowerCase()) ?? null;
      setInterviewRole(role);
    }
    setChatDraft('');
    setInterviewPicks([]);
    setChatMessages(prev => [...prev.map(m => m.choices ? {
      ...m,
      answered: true
    } : m), {
      id: Date.now(),
      role: 'user',
      text
    }]);
    if (next < ONBOARDING_INTERVIEW.length) {
      setInterviewIndex(next);
      pushInterviewQuestion(next, role, 1000);
      return;
    }
    // Final turn: state what the interview actually produced.
    setInterviewIndex('done');
    const id = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: [...ONBOARDING_SUMMARY.work]
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...ONBOARDING_SUMMARY.answer],
        created: ONBOARDING_SUMMARY.created
      } : m));
    }, 1500);
  };
  const sendChatMessage = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    if (typeof interviewIndex === 'number') {
      answerInterview(clean);
      return;
    }
    // The credit welcome is a first-run message. Once they've asked something
    // it has done its job, so it never returns — even on a new chat.
    setChatCreditNoticeSeen(true);
    const script = chatScripts[clean] ?? {
      steps: [{
        icon: 'gmail',
        label: 'Searched your connected tools'
      }],
      answer: ["Here's what I found across Gmail, Drive and Calendar. Ask me to act on any of it and I'll take it from there."],
      sources: [{
        icon: 'gmail',
        label: 'Gmail'
      }, {
        icon: 'googledrive',
        label: 'Drive'
      }]
    };
    const base = Date.now();
    setChatDraft('');
    setChatMessages(prev => [...prev, {
      id: base,
      role: 'user',
      text: clean
    }, {
      id: base + 1,
      role: 'giga',
      thinking: true,
      steps: script.steps
    }]);
    // Reveal the steps, then the answer, so the thread reads as work being
    // done rather than a block of text appearing at once.
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === base + 1 ? {
        ...m,
        thinking: false,
        answer: script.answer,
        sources: script.sources
      } : m));
    }, 1600);
  };
  useEffect(() => {
    if (chatMessages.length > 0) {
      chatThreadRef.current?.scrollTo({
        top: chatThreadRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatMessages]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Step 10: once the user signs out of all workspaces, the remembered
  // workspace list disappears and the screen falls back to a clean sign-in.
  const [signedOut, setSignedOut] = useState(false);
  // Step 10: only the most recently used workspace is shown by default. The
  // full list is one click away, so returning users get a single obvious way
  // back in rather than a wall of near-identical rows competing with the
  // auth buttons above them.
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  // Step 10: Microsoft Teams isn't live yet, so the "Join Microsoft Teams
  // waitlist" button opens an inline email capture and confirms once submitted.
  const [teamsWaitlistOpen, setTeamsWaitlistOpen] = useState(false);
  const [teamsWaitlistEmail, setTeamsWaitlistEmail] = useState('');
  const [teamsWaitlistDone, setTeamsWaitlistDone] = useState(false);
  const submitTeamsWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamsWaitlistEmail.trim()) return;
    setTeamsWaitlistDone(true);
  };
  // Toggle a tool in/out of the selected set (used by the step 3 tool grid)
  const toggleTool = (id: string) => {
    setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [toolSearch, setToolSearch] = useState('');
  // Step 5: "Tell us more about you" select answers
  const [foundUs, setFoundUs] = useState('');
  const [role, setRole] = useState('');
  // Step 5: what kind of business the user runs (required, searchable)
  const [businessType, setBusinessType] = useState('');
  // Step 5: which AI Teammate(s) the user wants to create first (required, multi-select)
  const [firstTeammates, setFirstTeammates] = useState<string[]>([]);
  const toggleFirstTeammate = (option: string) => setFirstTeammates(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  // Step 6: Functional roles for tool recommendations (multi-select)
  const [funcRoles, setFuncRoles] = useState<string[]>([]);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  // Step 6: integrations the user requested when not found in the catalog
  const [requestedTools, setRequestedTools] = useState<string[]>([]);
  // Step 6: optional link the user can paste for a tool we don't list yet
  const [requestedToolLink, setRequestedToolLink] = useState('');
  // Step 11: selected trial plan
  const [plan, setPlan] = useState('plus');
  // Step 11: mobile-only order-summary disclosure (Stripe Checkout pattern)
  const [payDetailsOpen, setPayDetailsOpen] = useState(false);
  // Step 7: per-tool OAuth-style connection flow
  const [connectedConns, setConnectedConns] = useState<string[]>([]);
  // Whether the user has kicked off the connection sequence with the start button
  const [connectionsStarted, setConnectionsStarted] = useState(false);
  const [permModal, setPermModal] = useState<null | {
    id: string;
    name: string;
    icon: string;
    scopes: any[];
  }>(null);
  const [connectPhase, setConnectPhase] = useState<'consent' | 'handing-off' | null>(null);
  // Step 3: Slack OAuth consent — per-permission checkbox state. The two
  // REQUIRED scopes stay locked on; the rest are user-toggleable.
  const [slackOAuthScopes, setSlackOAuthScopes] = useState<Record<string, boolean>>({
    readChannels: true,
    searchHistory: true,
    readFiles: true,
    readDMs: false,
    postOnBehalf: true
  });
  // Step 7: local state for optional permission checkboxes in the modal
  const [optionalScopes, setOptionalScopes] = useState<Record<string, boolean>>({});
  // Step 7: per-tool integration visibility — 'private' (only you) or 'team'
  // (shared with the whole workspace).
  const [connVisibility, setConnVisibility] = useState<Record<string, 'private' | 'team'>>({});
  const USE_CASE_RECS: Record<string, string[]> = {
    'Marketing & Ad Campaigns': ['googleanalytics', 'googleads', 'mailchimp', 'hubspot', 'notion', 'slack'],
    'Operations & Workflow Automation': ['notion', 'slack', 'googledrive', 'asana', 'monday', 'quickbooks'],
    'Data analysis & reporting': ['googleanalytics', 'mixpanel', 'amplitude', 'notion', 'slack', 'github'],
    'Sales & CRM': ['salesforce', 'hubspot', 'pipedrive', 'slack', 'gmail', 'googledrive'],
    'Content & Creative Production': ['figma', 'miro', 'notion', 'slack', 'googledrive', 'dropbox'],
    'Engineering & Development': ['github', 'linear', 'slack', 'sentry', 'googledrive', 'notion'],
    'Executive / Leadership Assistant': ['slack', 'gmail', 'notion', 'googledrive', 'stripe', 'salesforce'],
    'Agency/Consultancy & Client Operations': ['slack', 'notion', 'googledrive', 'asana', 'trello', 'zoom'],
    'eCommerce & Shopify': ['shopify', 'stripe', 'mailchimp', 'googleanalytics', 'slack', 'zendesk'],
    Other: ['slack', 'notion', 'googledrive', 'gmail']
  };
  useEffect(() => {
    if (step === 6 && selectedTools.length === 0) {
      const activeRoles = funcRoles.length > 0 ? funcRoles : firstTeammates.length > 0 ? firstTeammates : ['Engineering & Development'];
      if (funcRoles.length === 0) {
        setFuncRoles(activeRoles);
      }
      const initialTools = new Set<string>();
      activeRoles.forEach(r => {
        USE_CASE_RECS[r]?.forEach(t => initialTools.add(t));
      });
      if (initialTools.size === 0) {
        USE_CASE_RECS['Engineering & Development'].forEach(t => initialTools.add(t));
      }
      // A Google signup already granted Gmail, Drive and Calendar during
      // consent, so those always appear (and show as connected) rather than
      // being offered again as something to set up.
      if (authProvider === 'google') {
        GOOGLE_WORKSPACE_TOOLS.forEach(t => initialTools.add(t));
      }
      setSelectedTools(Array.from(initialTools));
    }
  }, [step, firstTeammates, funcRoles.length, selectedTools.length, authProvider]);
  // Step 7: connections to make — derived from the tools the user selected.
  // Falls back to a sensible default set when nothing was selected (e.g. when
  // the screen is opened directly via screen state).
  const step7Connections = useMemo(() => {
    const ids = selectedTools.length > 0 ? selectedTools : ['slack', 'googledrive', 'notion', 'github', 'linear', 'sentry'];
    return ids.map(id => {
      const tool = ALL_TOOLS.find(t => t.id === id) || {
        id,
        name: id,
        icon: id,
        category: 'other'
      };
      return {
        ...tool,
        scopes: getScopesForTool(tool)
      };
    });
  }, [selectedTools]);
  const step7ActiveIdx = step7Connections.findIndex(c => !connectedConns.includes(c.id));
  const step7AllDone = step7Connections.length > 0 && connectedConns.length === step7Connections.length;
  // Where setup ends. A Slack signup gets handed off to Slack; a Google signup
  // has nothing to hand off to, so it lands in Giga's own web chat.
  const finalStep = authProvider === 'google' ? 22 : 8;
  // Landing/left-pane animation state. These hooks MUST live above the
  // per-step early returns below — otherwise renders that return early would
  // run fewer hooks than renders that fall through, tripping React #300.
  const LANDING_TESTIMONIAL_COUNT = 3;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % LANDING_TESTIMONIAL_COUNT);
    }, 7000);
    return () => clearInterval(testimonialInterval);
  }, []);
  // Setup checklist progress — the rows resolve one-by-one (pending → done) so
  // the card reads like a live, sub-60-second setup actually happening.
  const SETUP_STEPS = 3;
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => {
        setSetupStep(prev => {
          if (prev >= SETUP_STEPS) {
            clearInterval(id);
            return prev;
          }
          return prev + 1;
        });
      }, 750);
      (window as any).__gigaSetupInterval = id;
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval((window as any).__gigaSetupInterval);
    };
  }, []);
  // Step 22: where a Google signup lands. There is no Slack to hand off to,
  // so Giga's own web chat is the product surface. Opens on a real composer
  // with starters drawn from what was just connected, so the first question is
  // one click rather than a blank page. Slack is offered, not required.
  if (step === 22) {
    const starters = ["What changed in my inbox while I was out?", 'Summarise the deck in Drive from yesterday', "Who am I meeting tomorrow, and what do I need?"];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className={`min-h-screen bg-[#FAF8F4] font-body transition-[padding] duration-250 ease-[cubic-bezier(0.23,1,0.32,1)] ${chatNavOpen ? 'md:pl-[272px]' : ''}`} data-id="element-11165" data-mp="238eoqwpjvib9">
        {/* Collapsed rail: just the mark and the one control that reveals the
         nav. Mirrors the reference — brand top-left, nothing else claiming
         space until the user asks for it. Hidden while the nav is pinned open,
         since the panel carries its own header. */}
        <div className={`fixed left-0 top-0 z-30 flex items-center gap-1 p-3 ${chatNavOpen ? 'md:hidden' : ''}`} data-id="element-11166" data-mp="1thtjiqfdffjd">
          <Link to="/" aria-label="Brightwave workspace" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white transition-transform hover:scale-105" data-id="element-11167" data-mp="1ya3guritfhvo">
            BW
          </Link>
          <button type="button" onClick={() => setChatNavOpen(true)} aria-label="Open navigation" aria-expanded={chatNavOpen} className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11168" data-mp="15qee2osasi3w">
            <PanelLeft size={17} data-id="element-11169" data-mp="87o9k8jfmjd3" />
          </button>
        </div>

        {/* Top-right: what needs your attention, and the one human escape
         hatch. Both stay out of the composer's way. */}
        <div className="fixed right-0 top-0 z-30 flex items-center gap-1.5 p-3" data-id="element-11170" data-mp="i2pyb39spjf5">
          {/* Once a thread exists it has weight and an audience — its size and
           who can see it belong here, not buried in a settings panel. */}
          {chatView === 'chat' && chatMessages.length > 0 && <span className="mr-1 hidden items-center gap-2.5 text-[12.5px] text-neutral-500 sm:flex" data-id="element-11171" data-mp="mw2d6looabup">
              <span data-id="element-11172" data-mp="10vk539x9m58a">
                {(0.01 + chatMessages.length * 0.006).toFixed(2)} MB
              </span>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11173" data-mp="vx8ugwz5bqp0">
                <Copy size={12} className="text-neutral-400" data-id="element-11174" data-mp="1kh3s2r0fiv54" />
                Private
              </button>
            </span>}
          {/* Setup progress sits with the other status affordances rather than
           floating over the composer. A ring and a count — status, not promo. */}
          <button type="button" onClick={() => setChatChecklistOpen(o => !o)} aria-expanded={chatChecklistOpen} className={`relative flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3 transition-colors ${chatChecklistOpen ? 'border-[#E2DACD] bg-white' : 'border-[#EFEAE1]/80 bg-white/70 backdrop-blur-sm hover:border-[#E2DACD] hover:bg-white'}`} data-id="element-11175" data-mp="wr9gecrujcmg">
            {/* A single unresolved to-do. Red rather than brand orange, because
             its job is to be the one thing on screen you want to clear. */}
            {interviewIndex === null && <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#DC2626] px-1 text-[10.5px] font-bold leading-none text-white ring-2 ring-[#FAF8F4]" data-id="element-11176" data-mp="ht7dm4sn2v69">
                <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#DC2626] opacity-60" data-id="element-11177" data-mp="35wex30cxhv0s" />
                1
              </span>}
            <svg viewBox="0 0 20 20" className="h-[15px] w-[15px] -rotate-90" aria-hidden="true" data-id="element-11178" data-mp="3fuvv2haab9wc">
              <circle cx="10" cy="10" r="8" fill="none" stroke="#EFEAE1" strokeWidth="3" data-id="element-11179" data-mp="3jwhgir0vwwi5" />
              <circle cx="10" cy="10" r="8" fill="none" stroke="#2B211A" strokeWidth="3" strokeLinecap="round" strokeDasharray={2 * Math.PI * 8} strokeDashoffset={2 * Math.PI * 8 * (1 - setupProgress.ratio)} data-id="element-11180" data-mp="3ourbp1mlzx3r" />
            </svg>
            <span className="hidden text-[12.5px] font-medium text-neutral-500 sm:inline" data-id="element-11181" data-mp="2eep3l7dhpn6">
              Finish setup
            </span>
            <span className="text-[12.5px] text-neutral-300" data-id="element-11182" data-mp="7cg7e3wz5kxl">{setupProgress.done}/{setupProgress.total}</span>
          </button>
          <div className="relative" data-id="element-11183" data-mp="1vxx4zrfqk8gu">
            <button type="button" onClick={() => setChatNotifOpen(o => !o)} aria-label="Notifications" aria-expanded={chatNotifOpen} className="relative flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11184" data-mp="1vyygfx0nywz3">
              <Bell size={17} data-id="element-11185" data-mp="1hz76q8h6k2qw" />
              <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-[#EA580C] ring-2 ring-[#FAF8F4]" data-id="element-11186" data-mp="1hzysysow0o4i" />
            </button>
            <AnimatePresence data-id="element-11187" data-mp="2an2yqzvt16hc">
              {chatNotifOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setChatNotifOpen(false)} data-id="element-11188" data-mp="2anuv4cx8788o" />
                  <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11189" data-mp="25uibkh7elrms">
                    <p className="border-b border-[#F4F0E9] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11190" data-mp="mxf1be8nis2l">
                      Notifications
                    </p>
                    <ul className="divide-y divide-[#F4F0E9]" data-id="element-11191" data-mp="cz906yuo5pys">
                      {[{
                    title: 'Gmail, Drive and Calendar connected',
                    time: 'Just now'
                  }, {
                    title: 'Your $100 in credits is active',
                    time: 'Just now'
                  }].map(n => <li key={n.title} className="px-4 py-3" data-id="element-11192" data-mp="1tdh9vsucc4t0">
                          <p className="text-[13.5px] font-medium leading-snug text-[#2B211A]" data-id="element-11193" data-mp="1ycttm4px0vrd">
                            {n.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-neutral-400" data-id="element-11194" data-mp="28hacl6yoysze">
                            {n.time}
                          </p>
                        </li>)}
                    </ul>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>
        </div>

        {/* Setup checklist, hung off the top-right control. Only the step you
         are on expands, so the panel stays short. */}
        <div className="fixed right-3 top-[52px] z-40 w-[300px]" data-id="element-11195" data-mp="2m85lukd3rc3i">
          <AnimatePresence initial={false} data-id="element-11196" data-mp="2m7xaiwwmoeuf">
            {chatChecklistOpen && <motion.div key="checklist" initial={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_28px_70px_-28px_rgba(43,33,26,0.4)]" data-id="element-11197" data-mp="306wwtxwo4hnf">
                <div className="border-b border-[#F4F0E9] px-4 py-3.5" data-id="element-11198" data-mp="18aop7ry3busn">
                  <div className="flex items-center justify-between gap-3" data-id="element-11199" data-mp="1cbgxoiz51vnv">
                    <span className="text-[14px] font-semibold text-[#2B211A]" data-id="element-11200" data-mp="1h9igr3wkduzc">
                      Finish setup
                    </span>
                    <button type="button" onClick={() => setChatChecklistOpen(false)} aria-label="Close checklist" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-11201" data-mp="kkubl85y627c">
                      <X size={15} data-id="element-11202" data-mp="ti4p7068m3ff" />
                    </button>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5" data-id="element-11203" data-mp="yb677h1qqj38">
                    <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#F0EBE2]" data-id="element-11204" data-mp="vjym1xl5vcx">
                      <div className="h-full rounded-full bg-[#2B211A] transition-[width] duration-300 ease-out" style={{
                    width: `${setupProgress.ratio * 100}%`
                  }} data-id="element-11205" data-mp="5ttr24rxehuu" />
                    </div>
                    <span className="text-[12px] text-neutral-400" data-id="element-11206" data-mp="3phx00tw1h2na">
                      {setupProgress.done} of {setupProgress.total}
                    </span>
                  </div>
                </div>

                {/* The one outstanding thing, lifted out of the list. Everything
                 below is a task; this is an offer to do the tasks for you. */}
                {interviewIndex === null && <div className="border-b border-[#F4F0E9] bg-[#FFF6F2] px-4 py-3.5" data-id="element-11207" data-mp="36pcnin0nda5n">
                    <p className="flex items-center gap-2 text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11208" data-mp="31qjcr2cqvx96">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#DC2626]" data-id="element-11209" data-mp="2wt9cssrqc373" />
                      Let Giga set you up
                    </p>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11210" data-mp="2ssfnpw2jwicm">
                      A short conversation. Giga learns your role, opens your
                      tracks and schedules your first pulses from the answers.
                    </p>
                    <button type="button" onClick={startOnboarding} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#EA580C] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-11211" data-mp="2i72gy8mgz6in">
                      Start
                      <ArrowRight size={13} data-id="element-11212" data-mp="2wtvr6zh4tm3z" />
                    </button>
                  </div>}

                <ul className="divide-y divide-[#F4F0E9]" data-id="element-11213" data-mp="31u082qlyciuq">
                  {[{
                label: 'Create your Giga account',
                done: true
              }, {
                label: 'Onboard with Giga',
                done: interviewIndex === 'done',
                body: interviewIndex === 'done' ? 'Done — your tracks, pulses and memory files are running.' : 'Answer a few questions in the chat and Giga builds the rest for you.',
                cta: interviewIndex === null ? 'Start onboarding' : undefined,
                action: 'onboard'
              }, {
                label: 'Connect your tools',
                done: true,
                body: 'Gmail, Drive and Calendar are in. Add Notion, Slack or Linear so Giga can act across the rest of your stack.',
                cta: 'Connect other tools',
                view: 'integrations'
              }, {
                label: 'Ask your first question',
                done: chatMessages.length > 0,
                body: 'Try one of the prompts above — it answers from the tools you just connected.'
              }, {
                label: 'Set up a Pulse',
                done: false,
                body: 'Put a recurring job on a schedule, like a Monday pipeline recap.',
                cta: 'Create a Pulse',
                view: 'pulses'
              }, {
                label: 'Build your first Workflow',
                done: false,
                body: 'Chain a few steps together so one prompt runs the whole job.',
                cta: 'Build a Workflow',
                view: 'journeys'
              }].map(item => {
                const open = chatChecklistStep === item.label;
                return <li key={item.label} data-id="element-11214" data-mp="2yp6bx3k39ub4">
                        <button type="button" onClick={() => setChatChecklistStep(open ? null : item.label)} className="flex w-full items-center gap-2.5 px-4 py-3 text-left" data-id="element-11215" data-mp="2yoet6tblbr29">
                          {item.done ? <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-11216" data-mp="38ezoo7vywfgy">
                              <Check size={11} strokeWidth={3.5} className="text-white" data-id="element-11217" data-mp="3dgp03on3qv5k" />
                            </span> : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-dashed border-neutral-300" data-id="element-11218" data-mp="3ibkswr05xmjq" />}
                          <span className={`flex-1 text-[13.5px] font-semibold ${item.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-11219" data-mp="1nzxpwh74afs9">
                            {item.label}
                          </span>
                          <ChevronDown size={14} className={`shrink-0 text-neutral-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-11220" data-mp="1ki08v18vdgf3" />
                        </button>
                        <AnimatePresence initial={false} data-id="element-11221" data-mp="1arnopa4yvp9h">
                          {open && item.body && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="overflow-hidden" data-id="element-11222" data-mp="16qbyp3i6l20r">
                              <div className="px-4 pb-4 pl-[42px]" data-id="element-11223" data-mp="1pgjwd634309k">
                                <p className="text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11224" data-mp="j1spmlrb4l7m">
                                  {item.body}
                                </p>
                                {item.cta && <button type="button" onClick={() => (item as any).action === 'onboard' ? startOnboarding() : openChatView((item as any).view === 'journeys' ? 'workflows' : (item as any).view === 'integrations' ? 'connections' : (item as any).view)} className="mt-2.5 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-11225" data-mp="3aiu3tx2bbdmk">
                                    {item.cta}
                                    <ArrowRight size={13} data-id="element-11226" data-mp="3pfuvy1of1alk" />
                                  </button>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </li>;
              })}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* DEBUG: preview the picker with an unconnected workspace. */}
        <button type="button" onClick={() => setChatNoConnections(v => !v)} title="Debug: empty workspace" aria-label="Debug: empty workspace" aria-pressed={chatNoConnections} className={`fixed bottom-4 left-4 z-30 flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${chatNoConnections ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-300 hover:text-[#2B211A]'}`} data-id="element-11227" data-mp="241yxh6zn6lq5">
          <Settings2 size={13} data-id="element-11228" data-mp="28o98njfxjzoe" />
        </button>

        {/* The sidebar itself — Tracks and Chats are the content; everything
         else is one quiet "More" disclosure at the bottom. */}
        <AnimatePresence data-id="element-11229" data-mp="2hn4c4nrlavo0">
          {chatNavOpen && <>
              {/* Scrim only on narrow screens, where the pinned panel would
               cover the content. On desktop the layout makes room instead. */}
              <motion.div key="nav-scrim" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} onClick={() => setChatNavOpen(false)} className="fixed inset-0 z-40 bg-[#2B211A]/20 md:hidden" data-id="element-11230" data-mp="2rltoy24mppxe" />
              <motion.aside key="nav-panel" initial={{
            x: -280,
            opacity: 0.6
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: -280,
            opacity: 0.6
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="fixed left-0 top-0 z-50 flex h-full w-[272px] flex-col border-r border-[#EFEAE1] bg-white" data-id="element-11231" data-mp="k91mglnfa9pf">
                <div className="flex items-center justify-between gap-2 px-3 py-3" data-id="element-11232" data-mp="137476o8xtkhf">
                  <span className="flex items-center gap-2" data-id="element-11233" data-mp="1huz30di8s4yq">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white" data-id="element-11234" data-mp="1cxgxyy7ci9w0">
                      BW
                    </span>
                    <span className="text-[14px] font-bold text-[#2B211A]" data-id="element-11235" data-mp="1mvws7a1o5z9q">
                      Brightwave
                    </span>
                  </span>
                  <button type="button" onClick={() => setChatNavOpen(false)} aria-label="Close navigation" className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11236" data-mp="25hkrki2spj0p">
                    <PanelLeft size={16} data-id="element-11237" data-mp="21hamr8qqoynf" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 pb-4" data-id="element-11238" data-mp="1msnxae7eeqih">
                  {/* Outlined rather than a solid black bar: it is the primary
                   action, but a filled slab dominates a panel this quiet. */}
                  <button type="button" onClick={() => {
                openChatView('chat');
                setChatMessages([]);
              }} className="mb-5 flex w-full items-center gap-2.5 rounded-xl border border-[#E7E0D4] bg-white px-3 py-2.5 text-[13.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-11239" data-mp="1dtspixoph8d2">
                    <SquarePen size={15} className="text-neutral-400" data-id="element-11240" data-mp="18v93kxa43wkp" />
                    New chat
                  </button>

                  {/* The four surfaces people set up early and revisit often —
                   promoted out of the launcher so they're one click, not two. */}
                  <ul className="mb-4" data-id="element-11241" data-mp="22cejwepdtkf8">
                    {[{
                  label: 'Assistants',
                  icon: UserCircle,
                  view: 'assistants'
                }, {
                  label: 'Pulses',
                  icon: Activity,
                  view: 'pulses'
                }, {
                  label: 'Connections',
                  icon: Plug,
                  view: 'connections'
                }, {
                  label: 'Groups',
                  icon: Users,
                  view: 'groups'
                }].map(item => <li key={item.label} data-id="element-11242" data-mp="3dscw2rfuwmko">
                        <button type="button" onClick={() => openChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-11243" data-mp="3drbjgij6zs0u">
                          <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-11244" data-mp="3q4fbrczhc1nx" />
                          {item.label}
                        </button>
                      </li>)}
                    {/* The occasional surfaces continue the same list rather
                     than living in a popover that covered the sidebar. */}
                    {chatNavMoreOpen && [{
                  label: 'Workflows',
                  icon: Network,
                  view: 'workflows'
                }, {
                  label: 'Skills',
                  icon: GraduationCap,
                  view: 'skills'
                }, {
                  label: 'Power Ups',
                  icon: Zap,
                  view: 'power-ups'
                }, {
                  label: 'Files',
                  icon: FolderOpen,
                  view: 'files'
                }, {
                  label: 'Tables',
                  icon: Database,
                  view: 'tables'
                }, {
                  label: 'Browser',
                  icon: Globe,
                  view: 'browser'
                }, {
                  label: 'Viewer',
                  icon: Monitor,
                  view: 'viewer'
                }].map(item => <li key={item.label} data-id="element-11245" data-mp="201og9q9oqspn">
                          <button type="button" onClick={() => setChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-11246" data-mp="2drrym65g229t">
                            <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-11247" data-mp="1ga09eeidrfp9" />
                            {item.label}
                          </button>
                        </li>)}
                    <li data-id="element-11248" data-mp="3izt4b5gu8sv9">
                      <button type="button" onClick={() => setChatNavMoreOpen(o => !o)} aria-expanded={chatNavMoreOpen} className="flex w-full items-center gap-1.5 rounded-lg px-2 py-[7px] text-left text-[13px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11249" data-mp="3j0cm8m6hnh9a">
                        {chatNavMoreOpen ? 'View less' : 'View more'}
                        <ChevronDown size={13} className={`transition-transform duration-200 ${chatNavMoreOpen ? 'rotate-180' : ''}`} data-id="element-11250" data-mp="5ik5357sxmyz" />
                      </button>
                    </li>
                  </ul>

                  {/* Six each, then a way to the full list — a sidebar that
                   scrolls for 18 rows stops being scannable. */}
                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-11251" data-mp="3mwidtxqmzilz">
                    Tracks
                  </p>
                  <ul className="mb-1" data-id="element-11252" data-mp="3hw42o6tqnsjq">
                    {TRACKS.filter(t => t.status === 'active').slice(0, 6).map(t => <li key={t.name} data-id="element-11253" data-mp="3cykpla8s1ltd">
                        <button type="button" onClick={() => openChatView('tracks')} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11254" data-mp="37zhm3u8g3nni">
                          <Folder size={14} strokeWidth={1.9} className="shrink-0 text-neutral-300" data-id="element-11255" data-mp="4klsg5a2juea" />
                          <span className="truncate" data-id="element-11256" data-mp="3vuu0joodtw95">{t.name}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('tracks')} className="mb-5 w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11257" data-mp="s953utof0nal">
                    All tracks
                  </button>

                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-11258" data-mp="zqumg2dn2sjm">
                    Chats
                  </p>
                  <ul className="mb-1" data-id="element-11259" data-mp="19pshlo5klkbl">
                    {['Inbox catch-up', 'Tomorrow’s meetings', 'Q3 pipeline review', 'Renewal risk in Salesforce', 'Draft reply to Acme legal', 'Summarise the board deck'].map(c => <li key={c} data-id="element-11260" data-mp="1iqgwsm6p9qq2">
                        <button type="button" onClick={() => openChatView('chats')} className="flex w-full items-center rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11261" data-mp="1ipf83ewrh5pv">
                          <span className="truncate" data-id="element-11262" data-mp="1xfd1qadgmnmg">{c}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('chats')} className="w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11263" data-mp="22fiwhzf10q8r">
                    All chats
                  </button>
                </div>

                {/* Account — usage, billing and settings belong to the person,
                 not the workspace, so they sit under the avatar rather than in
                 the feature launcher. */}
                <div className="relative border-t border-[#EFEAE1] p-3" data-id="element-11264" data-mp="3mxwmnkhzsfrq">
                  <button type="button" onClick={() => setChatAccountOpen(o => !o)} aria-expanded={chatAccountOpen} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#F6F2EA]" data-id="element-11265" data-mp="3rvy5c4biad2o">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EA580C] text-[11px] font-bold text-white" data-id="element-11266" data-mp="3i4ka48ql7zfc">
                      SC
                    </span>
                    <span className="min-w-0 flex-1 text-left" data-id="element-11267" data-mp="ehldsk44da81">
                      <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-11268" data-mp="1z6p3vtubt04c">
                        Sarah Chen
                      </span>
                      <span className="block truncate text-[11.5px] text-neutral-400" data-id="element-11269" data-mp="1u61etdpnqtcr">
                        $100 credit · 0 used
                      </span>
                    </span>
                    <ChevronDown size={14} className={`shrink-0 text-neutral-400 transition-transform duration-200 ${chatAccountOpen ? 'rotate-180' : ''}`} data-id="element-11270" data-mp="23ypag54wir9w" />
                  </button>
                  <AnimatePresence data-id="element-11271" data-mp="1bajmzamgqo6d">
                    {chatAccountOpen && <motion.ul initial={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute bottom-[calc(100%-4px)] left-3 right-3 z-10 origin-bottom rounded-xl border border-[#EFEAE1] bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11272" data-mp="1q6969109kn7e">
                        {[{
                    label: 'Usage & billing',
                    icon: CreditCard,
                    onSelect: () => {
                      setChatAccountOpen(false);
                      setChatBillingOpen(true);
                    }
                  }, {
                    label: 'Settings',
                    icon: Settings2,
                    // No dedicated settings view exists yet, so this lands on
                    // the dashboard root rather than a blank screen.
                    onSelect: () => navigate('/app-screen-mockups/dashboard')
                  }].map(item => <li key={item.label} data-id="element-11273" data-mp="228jxugecl163">
                            <button type="button" onClick={item.onSelect} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11274" data-mp="277dcwd9a908d">
                              <item.icon size={15} className="shrink-0 text-neutral-400" data-id="element-11275" data-mp="k92ml53xsz6u" />
                              {item.label}
                            </button>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </motion.aside>
            </>}
        </AnimatePresence>

        {/* Chat layout: greeting and starters occupy the empty thread area,
         composer parked at the bottom where it will stay once messages start
         filling the space above it. */}
        {chatView !== 'chat' ? <div className="min-h-screen" data-id="element-11276" data-mp="3nt74pjxzizt6">
            <WorkspaceViews view={chatView} data-id="element-11277" data-mp="3ist3oluswq4n" />
          </div> : <div className="flex h-screen flex-col" data-id="element-11278" data-mp="3irhqxwjdo81e">
          {chatMessages.length === 0 ? <div className="flex flex-1 items-center justify-center px-6 pt-20 md:pt-16" data-id="element-11279" data-mp="3dtg739e4o4ov">
              <div className="w-full max-w-2xl" data-id="element-11280" data-mp="1hq1rmbpj9rbs">
                <h1 className="text-center font-title text-3xl font-medium leading-tight tracking-tight text-[#2B211A] md:text-[38px]" data-id="element-11281" data-mp="1hrd0ur1qzraa">
                  {chatCreditNoticeSeen ? 'What can I help with?' : "You're all set, Sarah."}
                </h1>
                <p className="mx-auto mt-3.5 max-w-[38ch] text-center text-[15.5px] leading-relaxed text-neutral-500" data-id="element-11282" data-mp="1cz36mytlo527">
                  {chatCreditNoticeSeen ? 'Ask anything across your connected tools.' : <>
                      Ask anything across your connected tools. Your{' '}
                      <span className="font-semibold text-[#2B211A]" data-id="element-11283" data-mp="p2fmgz0nqemt">
                        $100 in credits
                      </span>{' '}
                      is ready — no card needed.
                    </>}
                </p>

                {/* The same offer as the red to-do, put where a new user is
                 already looking. One route, two entry points. */}
                {interviewIndex === null && <button type="button" onClick={startOnboarding} className="mx-auto mt-7 flex w-full max-w-md items-center gap-3 rounded-2xl border border-[#F3DECB] bg-[#FFF6F2] px-4 py-3.5 text-left transition-colors hover:border-[#EA580C]" data-id="element-11284" data-mp="cr517kywar89">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EA580C]" data-id="element-11285" data-mp="misr3hft007m">
                      <Sparkles size={15} className="text-white" data-id="element-11286" data-mp="hookx0ed3w7l" />
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-11287" data-mp="vk91hp6mc2bu">
                      <span className="block text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11288" data-mp="rhcfrpls7q3o">
                        Let me set you up first
                      </span>
                      <span className="block text-[12.5px] leading-snug text-neutral-500" data-id="element-11289" data-mp="10h95lujwqv0b">
                        A short conversation, then your tracks and pulses are running.
                      </span>
                    </span>
                    <ArrowRight size={15} className="shrink-0 text-[#EA580C]" data-id="element-11290" data-mp="329nn4sijhxg7" />
                  </button>}

                {/* Starters as light chips rather than full-width rows — they
                 sit closer to the composer and read as prompts, not a menu. */}
                <div className="mt-7 flex flex-wrap justify-center gap-2" data-id="element-11291" data-mp="1f65q3epqrmpq">
                  {starters.map(s => <button key={s} type="button" onClick={() => sendChatMessage(s)} className="rounded-full border border-[#EFEAE1] bg-white px-4 py-2 text-[13.5px] font-medium text-neutral-600 transition-colors hover:border-[#EA580C] hover:text-[#EA580C]" data-id="element-11292" data-mp="1k58v4z5q1sxp">
                      {s}
                    </button>)}
                </div>
              </div>
            </div> : <div ref={chatThreadRef} className="flex-1 overflow-y-auto px-6 pt-20 md:pt-16" data-id="element-11293" data-mp="1yuoh81ub5a72">
              <div className="mx-auto flex max-w-2xl flex-col gap-7 pb-6" data-id="element-11294" data-mp="22v8dz4ayybso">
                {chatMessages.map(m => m.role === 'user' ? <div key={m.id} className="flex justify-end" data-id="element-11295" data-mp="27sqdy570dgoj">
                      <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#2B211A] px-4 py-2.5 text-[15px] leading-relaxed text-white" data-id="element-11296" data-mp="2cuz6j0fyygq4">
                        {m.text}
                      </p>
                    </div> : <motion.div key={m.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex gap-3" data-id="element-11297" data-mp="2hy058cue6bgt">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#1F3A3D]" data-id="element-11298" data-mp="35qgk6eiu1nbw">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11299" data-mp="2vyj7f3c6enb2" />
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-11300" data-mp="3jodipgguk3t7">
                        {/* What it did, before what it found. */}
                        {m.steps && <ul className="mb-3 space-y-1" data-id="element-11301" data-mp="75ztq975r937">
                            {m.steps.map(s => <li key={s.label} className="flex items-center gap-2 text-[12.5px] text-neutral-400" data-id="element-11302" data-mp="bxzx6bmfsk4j">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" data-id="element-11303" data-mp="3tf11ufpklg1y" />
                                {s.label}
                                {m.thinking ? <Loader2 size={11} className="animate-spin text-neutral-300" data-id="element-11304" data-mp="pv6ee1riiarn" /> : <Check size={11} strokeWidth={3} className="text-neutral-300" data-id="element-11305" data-mp="uv18ee6l3ggx" />}
                              </li>)}
                          </ul>}
                        {m.thinking && <p className="text-[14.5px] text-neutral-400" data-id="element-11306" data-mp="kxmzr0qwjvz6">
                            Working…
                          </p>}
                        {m.answer && <div className="space-y-3" data-id="element-11307" data-mp="30rezfas2whgo">
                            {m.answer.map((para, i) => <p key={i} className="text-[15px] leading-relaxed text-[#2B211A]" dangerouslySetInnerHTML={{
                    __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                  }} data-id="element-11308" data-mp="35pylrcal3v9k" data-mpi={i} />)}
                          </div>}
                        {/* A Giga idea introduced at the moment it becomes
                         relevant, rather than in a tour up front. */}
                        {m.concept && <div className="mt-3.5 rounded-xl border border-[#F3DECB] bg-[#FFF9F5] px-4 py-3.5" data-id="element-11309" data-mp="150am1j11ocqq">
                            <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#EA580C]" data-id="element-11310" data-mp="101pv6a7on5ey">
                              {m.concept.label}
                            </p>
                            <p className="mt-1.5 text-[14px] font-semibold text-[#2B211A]" data-id="element-11311" data-mp="1924iff47iul3">
                              {m.concept.title}
                            </p>
                            {m.concept.lines.map((line, i) => <p key={i} className="mt-1.5 text-[13px] leading-relaxed text-neutral-600" data-id="element-11312" data-mp="hayawdm5puls" data-mpi={i}>
                                {line}
                              </p>)}
                          </div>}

                        {/* What the interview produced, as artefacts rather
                         than a paragraph claiming it happened. */}
                        {m.created && <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-11313" data-mp="298jn0w7yi5dc">
                            {m.created.map(c => <li key={c.title} className="rounded-xl border border-[#EFEAE1] bg-white px-3.5 py-3" data-id="element-11314" data-mp="249gv8dgzlvnb">
                                <p className="text-[13px] font-semibold text-[#2B211A]" data-id="element-11315" data-mp="1biormuxle4py">
                                  {c.title}
                                </p>
                                <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-500" data-id="element-11316" data-mp="1pjssr8x2kqy0">
                                  {c.detail}
                                </p>
                              </li>)}
                          </ul>}

                        {/* Suggested answers. They retire once answered so the
                         thread reads as a transcript, not a live form. */}
                        {m.choices && m.choices.length > 0 && !m.answered && <div className="mt-4" data-id="element-11317" data-mp="39f6q8eov6jia">
                            <div className="flex flex-wrap gap-2" data-id="element-11318" data-mp="39fyhxefcbx3a">
                              {m.choices.map(choice => {
                      const picked = interviewPicks.includes(choice);
                      return <button key={choice} type="button" onClick={() => m.multi ? setInterviewPicks(prev => picked ? prev.filter(p => p !== choice) : [...prev, choice]) : answerInterview(choice)} aria-pressed={m.multi ? picked : undefined} className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${picked ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-600 hover:border-[#EA580C] hover:text-[#EA580C]'}`} data-id="element-11319" data-mp="2zgzimmggtdsy">
                                    {m.multi && picked && <Check size={11} strokeWidth={3.5} className="mr-1.5 inline" data-id="element-11320" data-mp="9xfemqhe0kts" />}
                                    {choice}
                                  </button>;
                    })}
                            </div>
                            {m.multi && <button type="button" disabled={interviewPicks.length === 0} onClick={() => answerInterview(interviewPicks.join(', '))} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:bg-[#E7E0D4] disabled:text-neutral-400" data-id="element-11321" data-mp="jmw6vms14edp">
                                {interviewPicks.length === 0 ? 'Pick what applies' : `Continue with ${interviewPicks.length}`}
                                <ArrowRight size={13} data-id="element-11322" data-mp="3s2o5ztqeo1ug" />
                              </button>}
                          </div>}
                        {m.sources && <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-[#EFEAE1] pt-3" data-id="element-11323" data-mp="3o2nh1f5ok895">
                            <span className="text-[11.5px] font-medium uppercase tracking-wider text-neutral-400" data-id="element-11324" data-mp="3j1hnjf474gyk">
                              Sources
                            </span>
                            {m.sources.map(s => <span key={s.label} className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[12px] font-medium text-neutral-600" data-id="element-11325" data-mp="1tn8bpmqcamqn">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3 w-3 object-contain" data-id="element-11326" data-mp="23k3bsa29gv71" />
                                {s.label}
                              </span>)}
                          </div>}
                      </div>
                    </motion.div>)}
              </div>
            </div>}

          <div className="sticky bottom-0 bg-gradient-to-t from-[#FAF8F4] via-[#FAF8F4] to-transparent px-6 pb-6 pt-4" data-id="element-11327" data-mp="1jtrhihmaj5mg">
            <div className="mx-auto max-w-2xl" data-id="element-11328" data-mp="1oo5geu05vuvv">
              <div className="rounded-2xl border border-[#EFEAE1] bg-white p-4 shadow-[0_18px_50px_-28px_rgba(43,33,26,0.35)]" data-id="element-11329" data-mp="s4y56uz0eci5">
                <label htmlFor="giga-first-question" className="sr-only" data-id="element-11330" data-mp="w46wlt6ktyaj">
                  Ask Giga a question
                </label>
                <textarea id="giga-first-question" rows={1} value={chatDraft} onChange={e => setChatDraft(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage(chatDraft);
                }
              }} placeholder={typeof interviewIndex === 'number' ? ONBOARDING_INTERVIEW[interviewIndex].placeholder : 'Ask about anything in your connected tools…'} className="min-h-[28px] w-full resize-none bg-transparent text-[16px] leading-relaxed text-[#2B211A] outline-none placeholder:text-neutral-400" data-id="element-11331" data-mp="cysfuqdygpsd" />
                <div className="mt-2.5 flex items-center justify-between gap-4" data-id="element-11332" data-mp="10qz3901pdmfn">
                  {/* Scoping the chat, not advertising the tool count. Giga
                   already uses everything the user can see; this is for
                   narrowing it to one group, track or database. */}
                  <span className="relative flex flex-wrap items-center gap-1.5" data-id="element-11333" data-mp="1i0a2nqm4hdm9">
                    {/* One paperclip for both photos and files — the OS picker
                     already handles the distinction. */}
                    <button type="button" aria-label="Attach a photo or file" title="Attach a photo or file" className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-11334" data-mp="18b08843t3iuz">
                      <Paperclip size={15} data-id="element-11335" data-mp="zbmvn2kdye61" />
                    </button>
                    {chatContext.map(c => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#F4F0E9] py-1 pl-2.5 pr-1.5 text-[12.5px] font-medium text-[#2B211A]" data-id="element-11336" data-mp="139160xjsyrzm">
                        {c}
                        <button type="button" onClick={() => setChatContext(prev => prev.filter(v => v !== c))} aria-label={`Remove ${c}`} className="text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-11337" data-mp="ucjrdkc8cbz4">
                          <X size={12} data-id="element-11338" data-mp="2om491nf2ids7" />
                        </button>
                      </span>)}
                    <button type="button" onClick={() => setContextOpen(o => !o)} aria-expanded={contextOpen} className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-11339" data-mp="2n4yjvflln0vt">
                      <Plus size={13} data-id="element-11340" data-mp="28bvahvi5vtg6" />
                      Add context
                    </button>

                    <AnimatePresence data-id="element-11341" data-mp="35zvnoq8n2g36">
                      {contextOpen && <>
                          <div className="fixed inset-0 z-30" onClick={() => setContextOpen(false)} data-id="element-11342" data-mp="30z7y890267b2" />
                          <motion.div initial={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="absolute bottom-[calc(100%+8px)] left-0 z-40 w-[380px] max-w-[calc(100vw-32px)] origin-bottom-left overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11343" data-mp="2w6eqrdd7gkva">
                            {/* Tabs only earn their place once there is more
                             than one kind of context to switch between. With an
                             empty workspace they are five empty rooms. */}
                            {!chatNoConnections && <div className="flex gap-1 overflow-x-auto border-b border-[#F4F0E9] px-2 py-2" data-id="element-11344" data-mp="srmbys9qqas2">
                                {(['All', 'Groups', 'Tracks', 'Credentials', 'Databases'] as const).map(t => <button key={t} type="button" onClick={() => setContextTab(t)} className={`shrink-0 rounded-full px-2.5 py-1 text-[12.5px] font-semibold transition-colors ${contextTab === t ? 'bg-[#2B211A] text-white' : 'text-neutral-500 hover:bg-neutral-100'}`} data-id="element-11345" data-mp="12p0rmpnuavjb">
                                      {t}
                                    </button>)}
                              </div>}
                            {!chatNoConnections && <div className="border-b border-[#F4F0E9] p-2" data-id="element-11346" data-mp="1s2vn7vxa0nv2">
                                <input type="search" value={contextQuery} onChange={e => setContextQuery(e.target.value)} placeholder="Search context…" aria-label="Search context" className="w-full rounded-lg border border-[#EFEAE1] px-2.5 py-1.5 text-[13px] outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-400" data-id="element-11347" data-mp="14bq85iz5tzlt" />
                              </div>}
                            {/* Fixed height so switching tabs never resizes the
                             panel under the pointer. */}
                            <div className={`overflow-y-auto p-2 ${chatNoConnections ? '' : 'h-[280px]'}`} data-id="element-11348" data-mp="glm5bdtsq3vv">
                              {chatNoConnections ? (
                          /* Nothing set up yet. Each tab explains its own
                          primitive and offers the one action that creates
                          it, so the empty state is never a dead end. */
                          <div className="px-2 py-4" data-id="element-11349" data-mp="2j7hr8bze45vp">
                                  <p className="text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11350" data-mp="2o973tw29grlw">
                                    Nothing to draw on yet
                                  </p>
                                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11351" data-mp="3fyjzn3uuch6c">
                                    Context is what Giga is allowed to look at
                                    when it answers — the accounts it can sign
                                    into, the tracks and groups your work lives
                                    in, the tables it can query. Connect a tool
                                    and it starts filling in here.
                                  </p>
                                  <button type="button" onClick={() => {
                              setContextOpen(false);
                              setStep(6);
                            }} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-11352" data-mp="240c98q4amv2c">
                                    Connect your tools
                                    <ArrowRight size={14} data-id="element-11353" data-mp="1l7zybezkqlgl" />
                                  </button>
                                  <p className="mt-2 text-center text-[11.5px] text-neutral-400" data-id="element-11354" data-mp="1q6uoj1zvfcaq">
                                    Takes about a minute
                                  </p>
                                </div>) : (() => {
                            const sections = [{
                              label: 'Groups',
                              items: GROUP_NAMES
                            }, {
                              label: 'Tracks',
                              items: TRACKS.filter(t => t.status === 'active').slice(0, 8).map(t => t.name)
                            }, {
                              label: 'Credentials',
                              items: ['Gmail — sarah@brightwave.io', 'Google Drive', 'Google Calendar']
                            }, {
                              label: 'Databases',
                              items: ['Pipeline (Notion)', 'Support tickets (Airtable)']
                            }].filter(s => contextTab === 'All' || s.label === contextTab).map(s => ({
                              ...s,
                              items: s.items.filter(i => i.toLowerCase().includes(contextQuery.trim().toLowerCase()))
                            })).filter(s => s.items.length > 0);
                            if (sections.length === 0) {
                              return <p className="px-2 py-6 text-center text-[13px] text-neutral-400" data-id="element-11355" data-mp="2j2u3hr497nba">
                                      Nothing matches “{contextQuery}”.
                                    </p>;
                            }
                            return sections.map(s => <div key={s.label} className="mb-2 last:mb-0" data-id="element-11356" data-mp="29g9odqe16p0e">
                                      <p className="px-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11357" data-mp="1vgi79gs7wsi6">
                                        {s.label}
                                      </p>
                                      {s.items.map(item => {
                                const on = chatContext.includes(item);
                                return <button key={item} type="button" onClick={() => setChatContext(prev => on ? prev.filter(v => v !== item) : [...prev, item])} className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-11358" data-mp="2s5ps0ml0ixl1">
                                            <span className="truncate" data-id="element-11359" data-mp="3awqon3ynuh1f">{item}</span>
                                            {on && <Check size={14} strokeWidth={3} className="shrink-0 text-[#EA580C]" data-id="element-11360" data-mp="zi08061861q2" />}
                                          </button>;
                              })}
                                    </div>);
                          })()}
                            </div>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </span>
                  <button type="button" onClick={() => sendChatMessage(chatDraft)} disabled={!chatDraft.trim()} aria-label="Send" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${chatDraft.trim() ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-[#F0EBE2] text-neutral-400'}`} data-id="element-11361" data-mp="gim7t6cwjur4">
                    <Send size={15} data-id="element-11362" data-mp="lh7815s5mefk" />
                  </button>
                </div>
              </div>

              {/* Slack is optional, so it is one quiet line under the composer. */}
              <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-[13px] text-neutral-400" data-id="element-11363" data-mp="1wy6zy0alar92">
                <SlackIcon size={14} data-id="element-11364" data-mp="6hv18nhw1w6k" />
                Your team already lives in Slack?
                <button type="button" onClick={() => {
                setAuthProvider('slack');
                setStep(2);
              }} className="font-semibold text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-[#EA580C] hover:decoration-[#EA580C]" data-id="element-11365" data-mp="bg6b2y86o6os">
                  Add Slack
                </button>
              </p>
            </div>
          </div>
        </div>}

        {/* Same Usage & billing surface as the dashboard, opened on the
         starter-credit model this account is actually on. */}
        <BillingModal open={chatBillingOpen} onClose={() => setChatBillingOpen(false)} initialVariant="B" data-id="element-11366" data-mp="136b1pemjdpyg" />
      </motion.div>;
  }
  // Step 20: Google consent. Modelled on Google's own account-chooser +
  // scope screen. The Workspace scopes are requested here, in the same
  // consent, so the user arrives with Gmail, Drive and Calendar already
  // readable instead of facing an empty brain.
  if (step === 20) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11367" data-mp="1i9b2c52vict1">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11368" data-mp="25z5dmi7jntb6">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-11369" data-mp="210ltmncigrlg">
            <div className="mb-6 flex items-center justify-center" data-id="element-11370" data-mp="1w8tzk2u5c3qk">
              <GoogleIcon size={28} data-id="element-11371" data-mp="1w6zdumb1w7lf" />
            </div>
            <h1 className="mb-1 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-11372" data-mp="1s6p6p6bivb4z">
              Choose an account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-11373" data-mp="2orhh589xp0mw">
              to continue to <span className="font-semibold" data-id="element-11374" data-mp="2jwtqb2rgju55">Giga</span>
            </p>

            <ul className="mb-6 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200" data-id="element-11375" data-mp="i2p6e1o7i6ao">
              {[{
              name: 'Sarah Chen',
              email: 'sarah.chen@brightwave.io',
              initial: 'S',
              tone: 'bg-[#EA580C]'
            }, {
              name: 'Sarah Chen',
              email: 'sarah@gmail.com',
              initial: 'S',
              tone: 'bg-[#3D2F25]'
            }].map(acct => <li key={acct.email} data-id="element-11376" data-mp="49zkdx8oh1dk">
                  <button type="button" onClick={() => setStep(21)} className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-11377" data-mp="4b0w83xiprwc">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${acct.tone}`} data-id="element-11378" data-mp="3qgo1z279npnn">
                      {acct.initial}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-11379" data-mp="1oq2vrwu6zvm3">
                      <span className="block truncate text-[15px] font-semibold text-[#333333]" data-id="element-11380" data-mp="1onqdfikivj8i">
                        {acct.name}
                      </span>
                      <span className="block truncate text-[13px] text-neutral-500" data-id="element-11381" data-mp="34a3ht9aekazj">
                        {acct.email}
                      </span>
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-neutral-300 transition-colors group-hover:text-[#EA580C]" data-id="element-11382" data-mp="3e6oodz22lu5h" />
                  </button>
                </li>)}
              <li data-id="element-11383" data-mp="3nwrqcor66ify">
                <button type="button" onClick={() => setStep(21)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50" data-id="element-11384" data-mp="3nxayxeuxjuhb">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400" data-id="element-11385" data-mp="5osh2xyrilin">
                    <Plus size={16} data-id="element-11386" data-mp="27jkqjvws2k1v" />
                  </span>
                  <span className="text-[15px] font-medium text-neutral-600" data-id="element-11387" data-mp="22jeyiemlivj9">
                    Use another account
                  </span>
                </button>
              </li>
            </ul>

            <p className="text-center text-[12.5px] leading-relaxed text-neutral-400" data-id="element-11388" data-mp="1lp2nbkcaw8e6">
              To continue, Google will share your name, email address and
              profile picture with Giga.
            </p>

            <button type="button" onClick={() => setStep(1)} className="mx-auto mt-6 block text-[13px] font-semibold text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-11389" data-mp="1vff554sqdngm">
              Back
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 21: Google Workspace scope grant. Kept separate from the account
  // chooser so the permissions being granted are a deliberate read, not
  // something buried under an account row.
  if (step === 21) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11390" data-mp="2j4zzsky8y95q">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11391" data-mp="cik5to6jjj4z">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-11392" data-mp="2pwc52auxvah">
            <div className="mb-5 flex items-center justify-center gap-4" data-id="element-11393" data-mp="3twz3puuly3l8">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-neutral-200 bg-white shadow-sm" data-id="element-11394" data-mp="3twpei9he00gl">
                <GoogleIcon size={28} data-id="element-11395" data-mp="3kwj2ks1c78jj" />
              </div>
              <div className="flex gap-1" aria-hidden="true" data-id="element-11396" data-mp="3kuzliz7uwtgp">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11397" data-mp="3fwelp0wiobss" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11398" data-mp="3ayd1gcncu6fq" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11399" data-mp="3b07l7nmw3si8" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EA580C] shadow-sm" data-id="element-11400" data-mp="1e3qob86k0ikx">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11401" data-mp="194xd5meqp3nx" />
              </div>
            </div>

            <h1 className="mb-2 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-11402" data-mp="16apb7e19f0px">
              Giga wants access to your Google Account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-11403" data-mp="8ojmu8p6cqca">
              sarah.chen@brightwave.io
            </p>

            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11404" data-mp="djp42u7mtgu1">
              This will allow Giga to
            </p>
            <ul className="mb-6 space-y-2.5" data-id="element-11405" data-mp="ncwh918m614o">
              {[{
              label: 'Read your Gmail messages and threads',
              detail: 'So it can answer from your inbox and draft replies.'
            }, {
              label: 'See and open files in Google Drive',
              detail: 'So documents and spreadsheets become part of your brain.'
            }, {
              label: 'View and manage your Google Calendar',
              detail: 'So it can prep for meetings and schedule work.'
            }].map(scope => <li key={scope.label} className="flex gap-3 rounded-xl border border-neutral-200 p-3.5" data-id="element-11406" data-mp="39hejfodihups">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#EA580C]" data-id="element-11407" data-mp="34jwm6v8ux3xm" />
                  <span data-id="element-11408" data-mp="30jvw2dceb4aq">
                    <span className="block text-[14.5px] font-semibold text-[#333333]" data-id="element-11409" data-mp="2vrumx0aixtox">
                      {scope.label}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-relaxed text-neutral-500" data-id="element-11410" data-mp="2qtjcancenf5n">
                      {scope.detail}
                    </span>
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-col gap-3 sm:flex-row-reverse" data-id="element-11411" data-mp="2gtseocsjsk1c">
              <button type="button" onClick={() => {
              // Consent granted here, so these arrive already connected.
              setConnectedConns(prev => Array.from(new Set([...prev, ...GOOGLE_WORKSPACE_TOOLS])));
              setStep(5);
            }} className="w-full rounded-xl bg-[#2B211A] px-6 py-3.5 font-bold text-white transition-colors hover:bg-black sm:w-auto" data-id="element-11412" data-mp="2cu1ly3jgntu6">
                Allow
              </button>
              <button type="button" onClick={() => setStep(20)} className="w-full rounded-xl border border-neutral-300 px-6 py-3.5 font-bold text-[#2B211A] transition-colors hover:bg-neutral-50 sm:w-auto" data-id="element-11413" data-mp="1y8dxw7a50561">
                Cancel
              </button>
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-neutral-400" data-id="element-11414" data-mp="2c59j0y3nxn2g">
              You can revoke access at any time from your Google Account, or
              disconnect any tool from Giga's settings.
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // Step 2: Slack Consent (Workspace Selection)
  if (step === 2) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11415" data-mp="3j08m8trncayn">
        
        {/* Waitlist popup — opens immediately over the Slack connect screen
                                                                                  while the feature is invite-only. */}
        <AnimatePresence data-id="element-11416" data-mp="2w02h3d1hil98">
          {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => {
          setShowWaitlist(false);
          navigate('/');
        }} data-id="element-11417" data-mp="2w1djb8fa7ky8" />}
        </AnimatePresence>

        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11418" data-mp="352k59x0p9nyv">
          <div className="flex-1 p-6 sm:p-10 overflow-auto" data-id="element-11419" data-mp="kb1ky814dnzr">
            <h1 className="text-2xl font-title font-bold text-[#333333] text-center mb-6" data-id="element-11420" data-mp="2pyinntddr0kz">
              Sign in to Giga with Slack
            </h1>

            {/* Slack ⇄ Giga lockup */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-11421" data-mp="2uzgfkxo6z8zo">
              <div className="w-14 h-14 rounded-2xl bg-[#2B211A] flex items-center justify-center shadow-sm" data-id="element-11422" data-mp="2zv5a2mlctzp4">
                <SlackIcon size={28} data-id="element-11423" data-mp="33w78j17g67q1" />
              </div>
              <ArrowLeftRight size={22} className="text-neutral-400" data-id="element-11424" data-mp="38y69y5jmmukc" />
              <div className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-11425" data-mp="3dw7w4zccna01">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11426" data-mp="3imo9ykk30xmx" />
                
              </div>
            </div>

            {/* Account / workspace selector */}
            <p className="text-sm font-bold text-[#2B211A] mb-2" data-id="element-11427" data-mp="huugmb90qro">
              Select a Slack account or workspace
            </p>
            <button onClick={() => setStep(3)} className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-[#FFEDD5]/30 transition-all group mb-6 text-left" data-id="element-11428" data-mp="1g0snnlakstmk">
              
              <div className="flex items-center gap-3 min-w-0" data-id="element-11429" data-mp="1715utnvwww54">
                <div className="w-11 h-11 rounded-lg bg-[#611f69] flex items-center justify-center text-white font-bold shrink-0" data-id="element-11430" data-mp="23qn041qzgg6a">
                  F
                </div>
                <div className="min-w-0" data-id="element-11431" data-mp="1zqw3hherz5tu">
                  <div className="font-bold text-[#2B211A] text-[15px] truncate" data-id="element-11432" data-mp="1uwrt0o6awfo0">
                    Faith Alqahtani
                  </div>
                  <div className="text-xs text-neutral-500 truncate" data-id="element-11433" data-mp="1pwm7zqujdrew">
                    alqahtani.faith@gmail.com · trackrheadquarters.slack.com
                  </div>
                </div>
              </div>
              <ChevronDown size={18} className="text-neutral-400 group-hover:text-[#EA580C] shrink-0" data-id="element-11434" data-mp="2iljekwpdxzrt" />
              
            </button>

            {/* Account-details usage */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5 mb-6" data-id="element-11435" data-mp="36hy01qxdaun1">
              <p className="text-sm font-bold text-[#2B211A] mb-4" data-id="element-11436" data-mp="2sr2kjvsd5fah">
                Your account details will be used to:
              </p>
              <ul className="space-y-4" data-id="element-11437" data-mp="2iteon2j0u1qq">
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11438" data-mp="2isder1dqrlal">
                  <Plug size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11439" data-mp="2nshs4ijcrzwl" />
                  <span data-id="element-11440" data-mp="29sqc6c9a09fy">
                    Establish a verified connection between an existing Giga
                    account and Slack.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11441" data-mp="813ookolewo8">
                  <UserPlus size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11442" data-mp="7yzghqjnvbi5" />
                  
                  <span data-id="element-11443" data-mp="c0ckrw73v6my">
                    Help you create a new Giga account, if you don't already
                    have one.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11444" data-mp="1skz4c8fyqfqf">
                  <Sparkles size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11445" data-mp="26baz6f3xmsl9" />
                  
                  <span data-id="element-11446" data-mp="21j128f4egs9h">
                    Unlock Giga features in Slack, like configurable
                    notifications, rich link previews, and quick actions.
                  </span>
                </li>
              </ul>
            </div>

            {/* Privacy disclosure */}
            <p className="text-xs text-neutral-500 leading-relaxed mb-0 sm:mb-6" data-id="element-11447" data-mp="2p8c645h1r2ru">
              Slack will share your name, email address, profile image, user ID,
              and team ID with Giga. View Slack's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11448" data-mp="uvw095s5xqni">
                privacy policy
              </a>
              . By agreeing to allow Slack to share your identity, you are also
              agreeing to Giga's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11449" data-mp="926vz90el07e">
                privacy agreement
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11450" data-mp="du820d6z9uoz">
                terms of service
              </a>
              .
            </p>
          </div>

          <div className="p-6 border-t border-neutral-100 sm:border-none sm:p-0 sm:pb-10 sm:px-10 flex flex-col-reverse sm:flex-row items-center sm:justify-center gap-3 bg-white sticky bottom-0 z-10" data-id="element-11451" data-mp="3i7n18cdagegv">
            <button onClick={() => setStep(1)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl border border-neutral-300 text-[#2B211A] font-bold hover:bg-neutral-50 transition-colors" data-id="element-11452" data-mp="3r7a3t1hr7qbi">
              
              Cancel
            </button>
            <button onClick={() => {
            trackSignupSlackConnected();
            setStep(3);
          }} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-sm" data-id="element-11453" data-mp="16josv6ey93j3">

              Accept and Continue
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 3: Slack Permissions
  if (step === 3) {
    const viewItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-11454" data-mp="2had0f1k72shi" />,
      label: 'Content and info about channels & conversations'
    }, {
      icon: <LayoutGrid size={18} className="text-neutral-500" data-id="element-11455" data-mp="2d9jfmh21ttsu" />,
      label: 'Content and info about your workspace'
    }];
    const actionItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-11456" data-mp="239itkncsozky" />,
      label: 'Perform actions in channels & conversations'
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11457" data-mp="1ycimc947lqs9">
        
        {/* Thin top bar */}
        <div className="h-2 w-full bg-[#2B211A]" data-id="element-11458" data-mp="3smw10lg7f3qa" />

        {/* Slack header */}
        <header className="w-full border-b border-neutral-200 px-6 sm:px-10 py-5" data-id="element-11459" data-mp="o8fgi4i3lx1n">
          <div className="flex items-center gap-2" data-id="element-11460" data-mp="j9vwi9n2evbx">
            <SlackIcon size={28} data-id="element-11461" data-mp="xztn0w8gvx4a" />
            <span className="text-[22px] font-bold text-[#333333] tracking-tight" data-id="element-11462" data-mp="y033nt7mgrxb">
              slack
            </span>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 w-full max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-16 pb-32 sm:pb-16" data-id="element-11463" data-mp="5aw4qt49a0bj">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-11464" data-mp="cb4itp07gn3">
            {/* Left column */}
            <div data-id="element-11465" data-mp="f9v5zqmoytoi">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm mb-5" data-id="element-11466" data-mp="fad6izgdzu28">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 sm:w-8 sm:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11467" data-mp="acbqyoi66cvi" />
                
              </div>

              <h1 className="text-2xl sm:text-[32px] leading-tight font-title font-bold text-[#333333] mb-4" data-id="element-11468" data-mp="1tazw5i75exe3">
                Allow the "Giga" app to access Slack
              </h1>

              <div className="flex items-center gap-2 text-[#2B211A] font-bold text-sm sm:text-[15px] mb-2" data-id="element-11469" data-mp="1k9tdp3kxvci7">
                <CheckCircle size={18} className="text-[#2B211A]" data-id="element-11470" data-mp="1oa3qp075f36k" />
                App is approved by Slack
              </div>
              <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-11471" data-mp="2gyhq9kdvy7k8">
                Apps are reviewed for quality before they are listed in the
                Slack Marketplace.{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11472" data-mp="2786an0kua9xi">
                  Learn more about app reviews.
                </a>
              </p>

              <p className="text-sm sm:text-[15px] font-bold text-[#333333] mb-2" data-id="element-11473" data-mp="2uw63c61poaeq">
                Workspace
              </p>
              <button type="button" className="w-full max-w-md flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-neutral-300 bg-white hover:border-neutral-400 transition-colors text-left" data-id="element-11474" data-mp="39kpmlivi09nm">
                
                <div className="flex items-center gap-3 min-w-0" data-id="element-11475" data-mp="30j8ascp4iu4q">
                  <div className="w-7 h-7 rounded-md bg-[#4A90D9] flex items-center justify-center text-white text-[12px] font-bold shrink-0" data-id="element-11476" data-mp="2vlgg5ax3gtwu">
                    T
                  </div>
                  <span className="font-bold text-[#333333] text-[16px] truncate" data-id="element-11477" data-mp="2lvev6s01frm5">
                    Tonic
                  </span>
                </div>
                <ChevronDown size={18} className="text-neutral-500 shrink-0" data-id="element-11478" data-mp="2hubddzyfjlia" />
              </button>
            </div>

            {/* Right column */}
            <div data-id="element-11479" data-mp="27vcdb5rqcy6w">
              <h2 className="text-lg sm:text-[20px] font-bold text-[#333333] mb-4 sm:mb-5" data-id="element-11480" data-mp="27vvn1z788g9u">
                Review app permissions
              </h2>

              <div className="rounded-2xl bg-[#F8F8F8] border border-neutral-200/70 p-5 sm:p-6 space-y-6 sm:space-y-7" data-id="element-11481" data-mp="22ylxmjju84m0">
                <div data-id="element-11482" data-mp="1xrp1h5ltrshj">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-11483" data-mp="1xqw0ymdglz7u">
                    Information "Giga" can view
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-11484" data-mp="22rjnauqqtrvq">
                    {viewItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-11485" data-mp="27p3e0u4mnx4k" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-11486" data-mp="1f179524wmywp">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-11487" data-mp="1f1h0ou5lle4i">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-11488" data-mp="1os26b1joviwx" />
                      
                      </div>)}
                  </div>
                </div>

                <div data-id="element-11489" data-mp="wa6eureqbrjh">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-11490" data-mp="w9eqo1ngow38">
                    Actions "Giga" can take
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-11491" data-mp="2xxiz3qfifn5x">
                    {actionItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-11492" data-mp="2syxxbmkm0vfd" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-11493" data-mp="32ygfbxgym74s">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-11494" data-mp="32x5bxyrff1e7">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-11495" data-mp="3co8bux8mdabw" />
                      
                      </div>)}
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-[13px] text-neutral-500 leading-relaxed mt-5" data-id="element-11496" data-mp="3lpzfzxnih93n">
                Slack will share your granted permissions with Giga. View
                Slack's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11497" data-mp="1rczvpr86rqq1">
                  privacy policy
                </a>
                . By agreeing to allow Slack to share your granted permissions,
                you are also agreeing to Giga's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11498" data-mp="3t9iihncczv2m">
                  privacy agreement
                </a>{' '}
                and{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11499" data-mp="20utmxewtpqb">
                  terms of service
                </a>
                . Please review Giga's terms before installing.
              </p>

              <div className="fixed sm:static bottom-0 inset-x-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t border-neutral-200 sm:border-none flex flex-col-reverse sm:flex-row items-center sm:justify-end gap-3 mt-0 sm:mt-6 z-10" data-id="element-11500" data-mp="3p5atrq5wif4e">
                <button onClick={() => setStep(2)} className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg border border-neutral-300 text-[#333333] text-[15px] font-bold hover:bg-neutral-50 transition-colors" data-id="element-11501" data-mp="18i8btsgq74fr">
                  
                  Cancel
                </button>
                <button onClick={() => setStep(4)} className="w-full sm:w-auto px-7 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg bg-[#007A5A] hover:bg-[#148567] text-white text-[15px] font-bold transition-colors shadow-sm" data-id="element-11502" data-mp="1711gya0nzryw">
                  
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 4: Channel Selection
  if (step === 4) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11503" data-mp="1ptdhcr7rcj6e">
        
        <GigaHeader data-id="element-11504" data-mp="1gypgred4npnx" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-11505" data-mp="1gzibti2q4qq8">
          <div className="w-full max-w-md" data-id="element-11506" data-mp="2dig9wu3xjwfo">
            <div className="mb-7 sm:mb-10" data-id="element-11507" data-mp="2djho37g8sz1m">
              <div className="mb-2.5" data-id="element-11508" data-mp="28pvi1y4l3tes">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-11509" data-mp="28otu4t2gzcfn">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-11510" data-mp="7wz1trv6y7b">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-11511" data-mp="4bbf2kfszekr">
                  <SlackIcon size={24} data-id="element-11512" data-mp="99d0vd4m5rzx" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-11513" data-mp="j6987fis082w">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-11514" data-mp="nz2bs02idade">
              <button onClick={() => setStep(5)} className="w-full py-3.5 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors" data-id="element-11515" data-mp="ryt2k3s1bqhx">
                
                Invite Giga to public channels only
              </button>
              <button onClick={() => setStep(12)} className="w-full py-3.5 px-6 rounded-xl border-2 border-neutral-200 text-[#333333] font-medium hover:border-[#EA580C] hover:bg-[#FAF8F4]/30 transition-all" data-id="element-11516" data-mp="11umilx4yq24g">
                
                Select channels
              </button>
            </div>

            <div className="flex items-center gap-2.5 text-[13px] text-neutral-500 bg-neutral-50 rounded-xl px-4 py-3" data-id="element-11517" data-mp="ls5m617victh">
              <Info size={16} className="shrink-0" data-id="element-11518" data-mp="3q888019k4o0q" />
              <p className="[text-wrap:balance]" data-id="element-11519" data-mp="3q8zqqbi22r9l">
                You can remove Giga from any channel anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 12: Specific Channel Selection
  if (step === 12) {
    const DUMMY_CHANNELS = ['all-faith-test', 'new-channel', 'social'];
    // Auto-select all by default to match the screenshot's "3 selected" state
    // if the user hasn't interacted yet.
    const activeChannels = selectedChannels.length > 0 || !selectedChannels.includes('initialized') ? selectedChannels.includes('initialized') ? selectedChannels.filter(c => c !== 'initialized') : DUMMY_CHANNELS : [];
    const toggleChannel = (ch: string) => {
      let next = [...activeChannels];
      if (next.includes(ch)) {
        next = next.filter(c => c !== ch);
      } else {
        next.push(ch);
      }
      // Add a hidden flag so we know the user has interacted and we shouldn't auto-fill
      if (!next.includes('initialized')) next.push('initialized');
      setSelectedChannels(next);
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11520" data-mp="3g4t2fa59rpdl">
        
        <GigaHeader data-id="element-11521" data-mp="365u2qh2hf42q" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-11522" data-mp="365293bsg3gf3">
          <div className="w-full max-w-md" data-id="element-11523" data-mp="1pru89lmeo6mv">
            <div className="mb-7 sm:mb-10" data-id="element-11524" data-mp="1pqsnuqji27sh">
              <div className="mb-2.5" data-id="element-11525" data-mp="1urq2wu97qlpr">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-11526" data-mp="1usrnpqg16mko">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-11527" data-mp="zkieozk3ht8d">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-11528" data-mp="lliqtu4epid9">
                  <SlackIcon size={24} data-id="element-11529" data-mp="qjk4u0mz6rhv" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-11530" data-mp="btw5v629o4xb">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mb-4" data-id="element-11531" data-mp="2x5cj1f3n46f">
              {/* Search */}
              <div className="relative border-b border-neutral-200" data-id="element-11532" data-mp="3p2itugjgb2u5">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-11533" data-mp="3p4lqhjf3zu7u" />
                
                <input type="text" placeholder={`Search ${WORKSPACE_NAME} workspace`} className="w-full pl-11 pr-4 py-4 text-[15px] text-[#2B211A] placeholder:text-neutral-400 focus:outline-none bg-transparent" data-id="element-11534" data-mp="3aftucep78pnr" />
                
              </div>

              {/* List */}
              <div className="bg-[#FAF8F4]/50 p-2" data-id="element-11535" data-mp="3kbwsl9gxk45f">
                <div className="px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-11536" data-mp="3fen5i0h0k4kr">
                  {activeChannels.length} selected
                </div>
                <div className="flex flex-col gap-1 pb-2" data-id="element-11537" data-mp="3tclawgdcwqo4">
                  {DUMMY_CHANNELS.map(ch => {
                  const isSelected = activeChannels.includes(ch);
                  return <button key={ch} onClick={() => toggleChannel(ch)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors group" data-id="element-11538" data-mp="6yjvvp6cc5ts">
                        
                        <div className="flex items-center gap-3 text-[15px] text-[#2B211A]" data-id="element-11539" data-mp="fxoyb6l0wjb8">
                          <Hash size={16} className="text-neutral-400" data-id="element-11540" data-mp="bw3cgvqiguow" />
                          {ch}
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C] text-white' : 'border-neutral-300 text-transparent group-hover:border-neutral-400'}`} data-id="element-11541" data-mp="o07msoshfjby">
                          
                          <Check size={12} strokeWidth={3} data-id="element-11542" data-mp="xs3pkd5ijxmi" />
                        </div>
                      </button>;
                })}
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} disabled={activeChannels.length === 0} className="w-full py-4 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-sm" data-id="element-11543" data-mp="16ss1917fplw8">
              
              Invite Giga to {activeChannels.length} channels
            </button>

            <div className="flex items-center justify-center gap-2 text-[13px] text-neutral-500" data-id="element-11544" data-mp="3ihaw2v6a5eqx">
              <Info size={14} data-id="element-11545" data-mp="1zm6q1ykvt5ev" />
              <p data-id="element-11546" data-mp="1znhum0m5ih71">You can remove Giga from any channel anytime</p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 5: Tell us about you
  if (step === 5) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11547" data-mp="1qtbfqqgk4ji8">
        
        <GigaHeader data-id="element-11548" data-mp="2ijpqwex6rhq7" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-11549" data-mp="2ii53rad7b12d">
          <div className="w-full max-w-md" data-id="element-11550" data-mp="2ej5sl80vm82h">
            <div className="mb-7 sm:mb-10" data-id="element-11551" data-mp="29jb1p4h3pihf">
              <div className="mb-2.5" data-id="element-11552" data-mp="29i9tr8vdtc3x">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-11553" data-mp="24kglmaaiq810">
                  STEP 2 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-11554" data-mp="y5pc5i7bxevd">
                Tell us more about you
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-11555" data-mp="2h0y68c91jubh">
                A couple quick questions so Giga can hit the ground running.
              </p>
            </div>

            <div className="space-y-6 mb-8" data-id="element-11556" data-mp="28243s59149q0">
              <div data-id="element-11557" data-mp="27z852epcxh0i">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-11558" data-mp="2d0fe0h38km8r">
                  What best describes you?
                </label>
                <SearchableSelect value={role} onChange={setRole} searchable={false} options={['I am a founder or executive and wear many hats at my company', 'I am a real estate agent or real estate broker', 'I am a consultant or work with many different clients', 'I work in sales or rev ops', 'I work on a marketing or growth team', 'I work in finance or operations', 'I am a content creator or social media influencer', 'I am a product manager or work with an engineering team', 'Other']} placeholder="Select an option" ariaLabel="What best describes you?" data-id="element-11559" data-mp="30rb7rjr95rin" />
                
              </div>

              <div data-id="element-11560" data-mp="1cqwhjnlfe7lp">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-11561" data-mp="1cubv2lzgb8l0">
                  What kind of business is it?
                </label>
                <SearchableSelect value={businessType} onChange={setBusinessType} options={BUSINESS_TYPES} placeholder="Select an option" searchPlaceholder="Search business types…" ariaLabel="What kind of business is it?" data-id="element-11562" data-mp="1lt5pq6t81ovx" />
                
              </div>

              <div data-id="element-11563" data-mp="t1vobn7inboj">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-11564" data-mp="p2wlc85chozq">
                  What would Giga help you with? Select from use cases below:
                </label>
                <MultiSelectDropdown values={firstTeammates} onChange={toggleFirstTeammate} options={['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other']} placeholder="Select use cases" data-id="element-11565" data-mp="y3bdnrgq84cq" />
                
              </div>

              <div data-id="element-11566" data-mp="2aeow8j34bdoy">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-11567" data-mp="2adx2ldt2zq1b">
                  How did you find us?{' '}
                  <span className="text-neutral-400 font-normal" data-id="element-11568" data-mp="2k5tathovswj9">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-11569" data-mp="1mjch0mfcx129">
                  <select value={foundUs} onChange={e => setFoundUs(e.target.value)} className={`w-full px-4 py-3 rounded-xl border border-neutral-200 appearance-none bg-white focus:outline-none focus:border-[#EA580C] transition-colors ${foundUs ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11570" data-mp="1mkdqwnkmzhie">
                    
                    <option value="" disabled data-id="element-11571" data-mp="37vnou2kxsr53">
                      Select an option
                    </option>
                    <option data-id="element-11572" data-mp="3bwfwire5unz9">Google search</option>
                    <option data-id="element-11573" data-mp="3bv6169yoztwb">Slack Marketplace</option>
                    <option data-id="element-11574" data-mp="109vp6dgufjci">Community</option>
                    <option data-id="element-11575" data-mp="10bq9pqo7d9g2">Podcast</option>
                    <option data-id="element-11576" data-mp="w7ikzthqvthu">Reddit</option>
                    <option data-id="element-11577" data-mp="w8tyil0zsfm5">Product Hunt</option>
                    <option data-id="element-11578" data-mp="rgse2bto7il1">Facebook</option>
                    <option data-id="element-11579" data-mp="rfr307cnmw3b">YouTube</option>
                    <option data-id="element-11580" data-mp="mih3tzzgr62a">Twitter / X</option>
                    <option data-id="element-11581" data-mp="mhpjxmf8awru">Newsletter</option>
                    <option data-id="element-11582" data-mp="hj4ffasy1qxl">Other</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-11583" data-mp="hkfn3lpifitz" />
                  
                </div>
              </div>

              <div data-id="element-11584" data-mp="8ipw2gzjilwq">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-11585" data-mp="8kaoo12anury">
                  Keep me posted with Giga tips and updates
                </label>
                <div className="flex items-center gap-4" data-id="element-11586" data-mp="22vyvth2aat1s">
                  <button onClick={() => setEmailOptIn(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-11587" data-mp="1xwuokvyx75e3">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-11588" data-mp="1bptn2nhb1krv">
                      
                      {emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-11589" data-mp="11yywx80xu115"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-11590" data-mp="o3wuwvz7ovtm">Of course!</span>
                  </button>
                  <button onClick={() => setEmailOptIn(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-11591" data-mp="s0sxqy6in8zz">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${!emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-11592" data-mp="28h47z64yuzd6">
                      
                      {!emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-11593" data-mp="1tn4487zdswa"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-11594" data-mp="3t19baatwsdhw">No thanks</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => {
            trackSignupProfileCompleted(role, businessType);
            setStep(6);
          }} disabled={!role || !businessType || firstTeammates.length === 0} className={`w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${role && businessType && firstTeammates.length > 0 ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-300 text-white cursor-not-allowed'}`} data-id="element-11595" data-mp="6ryb8b6w5pat">
              
              Continue
              <ArrowRight size={18} data-id="element-11596" data-mp="2w8nlfp12fl3w" />
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 6: Tool Selection
  if (step === 6) {
    const CATEGORIES: {
      key: string;
      label: string;
    }[] = [{
      key: 'communication',
      label: 'COMMUNICATION'
    }, {
      key: 'docs',
      label: 'DOCS & DRIVE'
    }, {
      key: 'engineering',
      label: 'ENGINEERING'
    }, {
      key: 'project',
      label: 'PROJECT MANAGEMENT'
    }, {
      key: 'crm',
      label: 'CRM & SALES'
    }, {
      key: 'support',
      label: 'SUPPORT'
    }, {
      key: 'finance',
      label: 'FINANCE'
    }, {
      key: 'design',
      label: 'DESIGN'
    }, {
      key: 'analytics',
      label: 'ANALYTICS'
    }, {
      key: 'marketing',
      label: 'MARKETING'
    }];
    const filteredTools = ALL_TOOLS.filter(t => t.name.toLowerCase().includes(toolSearch.toLowerCase()));
    const POPULAR_TOOL_IDS = ['slack', 'gmail', 'googledrive', 'notion', 'github', 'linear', 'jira', 'salesforce', 'hubspot', 'stripe'];
    const popularTools = POPULAR_TOOL_IDS.map(id => ALL_TOOLS.find(t => t.id === id)).filter(Boolean) as typeof ALL_TOOLS;
    const isSearching = toolSearch.trim() !== '';
    const renderToolCard = (tool: (typeof ALL_TOOLS)[number]) => {
      const isSelected = selectedTools.includes(tool.id);
      return <button key={tool.id} onClick={() => toggleTool(tool.id)} aria-pressed={isSelected} className={`group relative p-3 rounded-2xl border transition-all duration-200 ease-out active:scale-[0.97] ${isSelected ? 'border-[#F8C9A4] bg-[#FFF7ED] shadow-[0_1px_3px_rgba(234,88,12,0.08)]' : 'border-neutral-200/80 bg-white hover:border-[#F3D2BB] hover:bg-[#FFFBF7]'}`} data-id="element-11597" data-mp="3acjb3ajr1pw7">
          
          <AnimatePresence data-id="element-11598" data-mp="2rgb8kei4ft4l">
            {isSelected && <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.5
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} className="absolute top-1.5 right-1.5 w-[18px] h-[18px] bg-[#EA580C] rounded-full flex items-center justify-center shadow-[0_1px_2px_rgba(234,88,12,0.3)]" data-id="element-11599" data-mp="2vhmsq4i87m5e">
              
                <Check size={11} strokeWidth={2.25} className="text-white" data-id="element-11600" data-mp="27sbecwld470v" />
              </motion.div>}
          </AnimatePresence>
          <ToolLogo name={tool.name} icon={tool.icon} className="w-8 h-8 mx-auto mb-1.5 transition-transform duration-200 group-hover:scale-105" fallbackTextSize="text-sm" data-id="element-11601" data-mp="2lq8eu42xsxkt" />
          
          <div className="text-xs font-medium text-[#333333] text-center leading-tight" data-id="element-11602" data-mp="1t3xdgr7iasmy">
            {tool.name}
          </div>
        </button>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11603" data-mp="22sozyjtsskqh">
        
        <GigaHeader hideOnMobile data-id="element-11604" data-mp="1y05mrnz0vrm7" />

        <div className="flex-1 px-6 pt-6 lg:pt-12 pb-36 overflow-auto" data-id="element-11605" data-mp="1abupeusjca2o">
          <div className="max-w-4xl mx-auto" data-id="element-11606" data-mp="15db50ytlb6cf">
            <div className="mb-2.5" data-id="element-11607" data-mp="15c9q2j9gdzpf">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-11608" data-mp="15b6zydjyg1ak">
                STEP 3 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-11609" data-mp="3lsy0h5cx0s7z">
              Where does your work actually live?
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-11610" data-mp="3c11y3i3sqfxy">
              GitHub, Linear, the docs you reference — pick everything. Giga
              reads across them with citations back to source.
            </p>

            <div className="relative mb-6 sm:mb-8" data-id="element-11611" data-mp="8iqvl82ca9vr">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} data-id="element-11612" data-mp="3qr99jdvdyyp5" />
              
              <input type="text" placeholder="Search tools..." value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 sm:py-3.5 rounded-xl border border-neutral-200 text-[#333333] bg-white focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 transition-all" data-id="element-11613" data-mp="3vo80jnjr6pj4" />
              
            </div>

            <div className="relative inline-block mb-7 sm:mb-10" data-id="element-11614" data-mp="2ussoha1rmvzg">
              <button onClick={() => setRoleMenuOpen(!roleMenuOpen)} aria-expanded={roleMenuOpen} aria-label={`Suggested for ${funcRoles.join(', ')}. Change use cases`} className="group inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full border border-neutral-200 bg-white text-sm shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-11615" data-mp="2ur7wns6u5r5a">
                
                <Sparkles size={14} className="text-neutral-400 shrink-0" data-id="element-11616" data-mp="3dhz7yzadvrmb" />
                <span className="text-neutral-500" data-id="element-11617" data-mp="39gdsd66gsz8f">Suggested for</span>
                <span className="font-semibold text-[#333333] max-w-[200px] truncate" data-id="element-11618" data-mp="34i2jaxnzusr9">
                  {funcRoles.length === 0 ? 'Select use cases' : funcRoles.length <= 1 ? funcRoles[0] : `${funcRoles.length} use cases`}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} data-id="element-11619" data-mp="1t6bdwnho6d88" />
                
              </button>

              <AnimatePresence data-id="element-11620" data-mp="dliupvs7rs2e">
                {roleMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleMenuOpen(false)} data-id="element-11621" data-mp="hmcnb2glpv1m" />
                  
                    <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-11622" data-mp="me4cp9o0tuq6">
                    
                      {Object.keys(USE_CASE_RECS).map(r => {
                    const isSelected = funcRoles.includes(r);
                    return <button key={r} onClick={e => {
                      e.preventDefault();
                      setFuncRoles(prev => {
                        const next = isSelected ? prev.filter(x => x !== r) : [...prev, r];
                        const newTools = new Set<string>();
                        next.forEach(role => {
                          USE_CASE_RECS[role]?.forEach(t => newTools.add(t));
                        });
                        setSelectedTools(Array.from(newTools));
                        return next;
                      });
                    }} className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'hover:bg-neutral-50 text-neutral-600'}`} data-id="element-11623" data-mp="eb5yp797u7gz">
                          
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-11624" data-mp="1azcaqf8gw25a">
                            
                              {isSelected && <Check size={12} strokeWidth={3} className="text-white" data-id="element-11625" data-mp="1ziw24h4hlhgz" />}
                            </div>
                            <span className="flex-1 leading-snug" data-id="element-11626" data-mp="1lminrgv91w6q">{r}</span>
                          </button>;
                  })}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {!isSearching && popularTools.length > 0 && <div className="mb-5 sm:mb-6" data-id="element-11627" data-mp="1gm4ooobmlwku">
                <h2 className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-[#EA580C] uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-11628" data-mp="11wgojqf6l3yp">
                  <Sparkles size={13} className="shrink-0" data-id="element-11629" data-mp="16wbdhofebjh4" />
                  Most popular
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-11630" data-mp="swacm8z4sc1n">
                  {popularTools.map(tool => renderToolCard(tool))}
                </div>
              </div>}

            {CATEGORIES.map(cat => {
            const items = filteredTools.filter(t => t.category === cat.key);
            if (items.length === 0) return null;
            return <div key={cat.key} className="mb-5 sm:mb-6" data-id="element-11631" data-mp="13dxh4qj8t825">
                  <h2 className="text-[11px] sm:text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-11632" data-mp="13gjnimuedhis">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-11633" data-mp="ufbq06zpgkpr">
                    {items.map(tool => renderToolCard(tool))}
                  </div>
                </div>;
          })}

            {filteredTools.length === 0 && (() => {
            const term = toolSearch.trim();
            const requested = term !== '' && requestedTools.includes(term.toLowerCase());
            return <div className="text-center py-12 px-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-[#FAF8F4]" data-id="element-11634" data-mp="bplv7jvxzyxu">
                    {requested ? <div className="max-w-md mx-auto" data-id="element-11635" data-mp="2icb3h6zbz7og">
                        <div className="w-12 h-12 rounded-full bg-[#FFEDD5] flex items-center justify-center mx-auto mb-4" data-id="element-11636" data-mp="2idvzl11amyof">
                          <Check size={22} className="text-[#EA580C]" data-id="element-11637" data-mp="2rcgahg0m4423" />
                        </div>
                        <h3 className="font-bold text-[#333333] text-lg mb-1" data-id="element-11638" data-mp="2wbkn6gmqwjx6">
                          On it
                        </h3>
                        <p className="text-neutral-600 text-sm" data-id="element-11639" data-mp="1zmlb0qj1ixzp">
                          We&rsquo;ve got your request for{' '}
                          <span className="font-semibold" data-id="element-11640" data-mp="23ksrrldi7zaw">{term}</span>
                          {requestedToolLink ? ' and the link' : ''}. If it has
                          an API or a login page, our team usually wires it up{' '}
                          <span className="font-semibold text-[#333333]" data-id="element-11641" data-mp="1fxkkixwimg68">
                            within 1 hour
                          </span>{' '}
                          — we&rsquo;ll email you the moment it&rsquo;s live.
                        </p>
                      </div> : <div className="max-w-xl mx-auto text-left" data-id="element-11642" data-mp="v9arrck0grg">
                        <h3 className="font-bold text-[#333333] text-lg mb-1 text-center" data-id="element-11643" data-mp="5x6zv27my5sb">
                          {term ? `Don't see ${term}?` : 'Looking for something specific?'}
                        </h3>
                        <p className="text-neutral-600 text-sm mb-5 text-center" data-id="element-11644" data-mp="fnt8udkujy21">
                          Giga connects to virtually anything. Tell us what you
                          need — drop a link and we&rsquo;ll usually have it
                          connected within 1 hour.
                        </p>
                        <label className="block text-[13px] font-semibold text-[#333333] mb-1.5" data-id="element-11645" data-mp="tjwyruootk8p">
                          Link{' '}
                          <span className="font-normal text-neutral-400" data-id="element-11646" data-mp="2vdwagh5q23lx">
                            (optional)
                          </span>
                        </label>
                        <input type="url" value={requestedToolLink} onChange={e => setRequestedToolLink(e.target.value)} placeholder="https://yourtool.com" className="w-full mb-4 px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#333333]/20 focus:border-[#333333]" data-id="element-11647" data-mp="30eahbvvl77id" />
                    
                        <button onClick={() => term && setRequestedTools(prev => [...prev, term.toLowerCase()])} disabled={!term} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-colors ${term ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`} data-id="element-11648" data-mp="2e27hjm69t89v">
                      
                          {term ? `Request ${term}` : 'Type a name to request it'}
                        </button>
                        <p className="flex items-start justify-center gap-1.5 text-xs text-neutral-400 leading-relaxed text-center mt-4" data-id="element-11649" data-mp="1qcd7tdh8zztu">
                          <Info size={13} className="shrink-0 mt-0.5" data-id="element-11650" data-mp="dfedkgl72wzr" />
                          <span data-id="element-11651" data-mp="de3543gt112b">
                            Connect anything later from Settings — API token,
                            MCP server, or OAuth.
                          </span>
                        </p>
                      </div>}
                  </div>;
          })()}

            <StickyRevealBar data-id="element-11652" data-mp="8ixnvhyckakk">
              <div className="flex items-center justify-between gap-3" data-id="element-11653" data-mp="3pxkxveappway">
                <button onClick={() => setStep(5)} aria-label="Back" className="flex items-center justify-center w-11 h-11 rounded-full text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors shrink-0" data-id="element-11654" data-mp="3pv86ybmz8blr">
                  
                  <ArrowLeft size={20} data-id="element-11655" data-mp="3luy1r170dp7y" />
                </button>
                <div className="flex items-center gap-2 sm:gap-4" data-id="element-11656" data-mp="3ltd95h498gcq">
                  <button onClick={() => setStep(7)} className="px-2 sm:px-4 py-3 text-sm font-medium text-neutral-500 hover:text-[#333333] transition-colors whitespace-nowrap" data-id="element-11657" data-mp="36z093swan30h">
                    
                    Later
                  </button>
                  <button onClick={() => setStep(7)} aria-label={selectedTools.length > 0 ? `Continue with ${selectedTools.length} tools` : 'Continue'} className="px-5 sm:px-7 py-3 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-11658" data-mp="2oaodds9zsdgy">
                    
                    Continue
                    {selectedTools.length > 0 && <span className="hidden sm:inline text-sm" data-id="element-11659" data-mp="3790mxf4tfpd2">
                        with {selectedTools.length}{' '}
                        {selectedTools.length === 1 ? 'tool' : 'tools'}
                      </span>}
                    <ArrowRight size={18} strokeWidth={2} data-id="element-11660" data-mp="3g12lf7vbqvkp" />
                  </button>
                </div>
              </div>
            </StickyRevealBar>
          </div>
        </div>
      </motion.div>;
  }
  // Step 7: Connections — per-tool OAuth permission flow
  if (step === 7) {
    const connections = step7Connections;
    const total = connections.length;
    const doneCount = connectedConns.length;
    const activeIdx = step7ActiveIdx;
    const allDone = step7AllDone;
    const allow = (conn: any) => {
      const next = connectedConns.includes(conn.id) ? connectedConns : [...connectedConns, conn.id];
      setConnectedConns(next);
      // Lock in the chosen visibility (default to shared with team if untouched)
      setConnVisibility(prev => ({
        ...prev,
        [conn.id]: prev[conn.id] ?? 'team'
      }));
      // Reset optional permission toggles for the next tool's fresh card
      setOptionalScopes({});
      if (next.length === total) {
        // All done — close the consent screen and advance.
        setConnectPhase(null);
        setPermModal(null);
        setTimeout(() => setStep(finalStep), 600);
      } else {
        // Visibility was set up front on the list, so once the user kicks off
        // the sequence we chain straight to the next tool's consent screen.
        const nextConn = connections.find(c => !next.includes(c.id));
        if (nextConn) {
          setPermModal(nextConn);
          setConnectPhase('consent');
        } else {
          setConnectPhase(null);
          setPermModal(null);
        }
      }
    };
    const handleAllowClick = (conn: any) => {
      setConnectPhase('handing-off');
      setTimeout(() => {
        allow(conn);
      }, 2000);
    };
    const startConnecting = () => {
      const firstConn = connections.find(c => !connectedConns.includes(c.id));
      if (!firstConn) return;
      setConnectionsStarted(true);
      setPermModal(firstConn);
      setConnectPhase('consent');
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11661" data-mp="3hl8fdhxxkff7">
        
        <GigaHeader hideOnMobile data-id="element-11662" data-mp="1ftlodge1gkiq" />

        <div className="flex-1 px-4 sm:px-6 py-6 sm:py-12 pb-40 overflow-auto" data-id="element-11663" data-mp="1fun96ckuwldn">
          <div className="max-w-4xl mx-auto" data-id="element-11664" data-mp="3a9aiwu6lo95v">
            <div className="mb-2.5" data-id="element-11665" data-mp="3a7he7kfjrz0i">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-11666" data-mp="3f0smzr3x6pv2">
                STEP 4 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-11667" data-mp="309jqqz5ii68y">
              Let's plug Giga into your stack.
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-11668" data-mp="3t0l9tbcbslxr">
              Approve each one to connect. You can adjust permissions anytime in
              Settings.
            </p>

            <div className="bg-neutral-50 rounded-2xl p-4 sm:p-6 mb-6" data-id="element-11669" data-mp="3j1m709e8rkio">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-11670" data-mp="3nznsf0z53vxb">
                <h2 className="text-lg sm:text-xl md:text-2xl font-title font-bold text-[#333333] italic" data-id="element-11671" data-mp="27mfqt8la0i41">
                  {total} connections to make
                </h2>
                <span className="text-xs sm:text-sm font-medium text-neutral-500" data-id="element-11672" data-mp="uo6y3legk52s">
                  {doneCount}/{total} done
                </span>
              </div>

              {/* progress bar */}
              <div className="h-1.5 w-full rounded-full bg-neutral-200 mb-5 sm:mb-6 overflow-hidden" data-id="element-11673" data-mp="13olh2e3y9834">
                <motion.div className="h-full rounded-full bg-[#EA580C]" animate={{
                width: `${doneCount / total * 100}%`
              }} transition={{
                type: 'spring',
                stiffness: 120,
                damping: 20
              }} data-id="element-11674" data-mp="znjk63xi984b" />
                
              </div>

              <div className="space-y-2" data-id="element-11675" data-mp="kq974q57ru6h">
                {connections.map(conn => {
                const connected = connectedConns.includes(conn.id);
                return <div key={conn.id} className={`flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-white border transition-all ${connected ? 'border-[#EA580C]' : 'border-neutral-200'}`} data-id="element-11676" data-mp="fxg2e29qwlue">
                      
                      {/* Tool info */}
                      <ToolLogo name={conn.name} icon={conn.icon} className="w-9 h-9 shrink-0" fallbackTextSize="text-sm" data-id="element-11677" data-mp="d478j889l37b" />
                      
                      <div className="min-w-0 flex-1" data-id="element-11678" data-mp="m3k8996l6deu">
                        <div className="font-bold text-sm sm:text-base text-[#333333] truncate" data-id="element-11679" data-mp="m2spx0202c6i">
                          {conn.name}
                        </div>
                        <div className="text-xs text-neutral-500" data-id="element-11680" data-mp="w29rp6e4s5x7">
                          {conn.scopes.length} permission
                          {conn.scopes.length > 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0" data-id="element-11681" data-mp="19t52io86wx3f">
                        {connected ? <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-[#FFF0E6] text-xs font-semibold text-[#EA580C]" data-id="element-11682" data-mp="19sd83gqbx5eq">
                            <span className="w-4 h-4 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-11683" data-mp="1jqj8tv0eg5i0">
                              <Check size={11} strokeWidth={3} className="text-white" data-id="element-11684" data-mp="1twaokd47ea7n" />
                            
                            </span>
                            Connected
                          </span> : <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-500" data-id="element-11685" data-mp="1jy4om11yje5f">
                            <span className="w-2 h-2 rounded-full border-[1.5px] border-neutral-300" data-id="element-11686" data-mp="2cmaaws8ntb7d" />
                            Pending
                          </span>}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar — keeps the primary action reachable as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         tool list scrolls. Hidden while a consent screen is open. */}
        {!permModal && <StickyRevealBar data-id="element-11687" data-mp="a40zc3tpqt72">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3" data-id="element-11688" data-mp="a2pwq7c07rhj">
              {allDone ? <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors shrink-0" data-id="element-11689" data-mp="ovrmrdzxf0u8">
              
                  Continue
                  <ArrowRight size={18} data-id="element-11690" data-mp="tu2wzpu4vdcz" />
                </button> : <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 shrink-0" data-id="element-11691" data-mp="1ch2bftu5nrad">
                  <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors" data-id="element-11692" data-mp="1hgezghwrj4ej">
                
                    {doneCount > 0 ? 'Finish the rest later' : "I'll connect later"}
                  </button>
                  <button onClick={startConnecting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors" data-id="element-11693" data-mp="tp9mcai7iq7x">
                
                    {doneCount > 0 ? 'Resume connecting' : 'Start connecting'}
                    <ArrowRight size={18} data-id="element-11694" data-mp="opeti1evfqk8" />
                  </button>
                </div>}
            </div>
          </StickyRevealBar>}

        {/* OAuth permission modal / full-screen flow — sits OUTSIDE the page
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         wrapper concerns; full-screen cover. */}
        <AnimatePresence data-id="element-11695" data-mp="2mf24xzh7qnj8">
          {permModal && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex flex-col items-center bg-white md:bg-[#F5F5F5] overflow-y-auto" data-id="element-11696" data-mp="2me0hevj0g8km">
            
              {connectPhase === 'consent' && <div className="w-full h-full flex flex-col items-center md:pt-12 md:pb-24 md:px-6" data-id="element-11697" data-mp="10wrg193c2z97">
                  {/* Fake URL Bar - Full width header on mobile, floating pill on desktop */}
                  <div className="flex items-center justify-between w-full md:max-w-md bg-neutral-100 md:bg-white md:rounded-full px-4 py-3 md:py-2 md:shadow-sm border-b md:border border-neutral-200 mb-0 md:mb-8 shrink-0" data-id="element-11698" data-mp="1atvzh15sogmb">
                    <div className="flex items-center gap-2 text-neutral-500" data-id="element-11699" data-mp="1jlxzwzfv5wwl">
                      <Lock size={14} className="text-[#EA580C]" data-id="element-11700" data-mp="1okj1p3arkon5" />
                      <span className="font-mono text-xs truncate max-w-[200px] sm:max-w-none" data-id="element-11701" data-mp="1tkwyfp6x0c5v">
                        {permModal.id}.com{' '}
                        <span className="text-neutral-400" data-id="element-11702" data-mp="1yifcftzshhkq">
                          /oauth/authorize
                        </span>
                      </span>
                    </div>
                    <span className="font-mono text-xs text-neutral-400 shrink-0" data-id="element-11703" data-mp="46qq36r8acqb">
                      {doneCount + 1} of {total}
                    </span>
                  </div>

                  {/* Consent Card - Full width on mobile, rounded card on desktop */}
                  <div className="w-full max-w-md bg-white md:rounded-3xl md:shadow-xl md:border border-neutral-200 flex flex-col min-h-screen md:min-h-0" data-id="element-11704" data-mp="23n90q0zzbzpv">
                    <div className="flex-1 overflow-auto" data-id="element-11705" data-mp="1yp7ifiado4fg">
                      <div className="p-6 md:p-8 pb-6 flex flex-col items-center" data-id="element-11706" data-mp="1ay263d3nbu9w">
                        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-11707" data-mp="16xi4nxc1i4hy">
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-neutral-200 flex items-center justify-center shadow-sm bg-white" data-id="element-11708" data-mp="1kon0eocaecn0">
                            <ToolLogo name={permModal.name} icon={permModal.icon} className="w-7 h-7 md:w-8 md:h-8" data-id="element-11709" data-mp="1fwdbnbmwrimb" />
                        
                          </div>
                          <ArrowRight size={16} className="text-neutral-300" data-id="element-11710" data-mp="n87ueyv2cbr8" />
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-11711" data-mp="2yw2u8ggdl1ps">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 md:w-8 md:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11712" data-mp="33ovyz4buga1v" />
                        
                          </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-title font-bold text-[#333333] text-center mb-3 leading-tight" data-id="element-11713" data-mp="3mmp144en8fy7">
                          Gigabrain is requesting access to your{' '}
                          {permModal.name} account
                        </h3>

                        <p className="text-[13px] md:text-[14px] text-neutral-500 text-center mb-6 max-w-sm leading-relaxed" data-id="element-11714" data-mp="1x78yl7v85sha">
                          Choose what Giga can do. You can change this anytime
                          in Settings → Connections, and revoke from{' '}
                          {permModal.name} directly.
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neutral-200 bg-neutral-50" data-id="element-11715" data-mp="1e3fhua7osv4d">
                          <div className="w-5 h-5 rounded-full bg-[#2B211A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-11716" data-mp="14d2njqa2lnl1">
                            L
                          </div>
                          <span className="text-sm text-neutral-700" data-id="element-11717" data-mp="uewln8o9khg6">
                            Signed in as{' '}
                            <span className="font-semibold" data-id="element-11718" data-mp="qc9s93by2ogv">l@acme.co</span>
                          </span>
                        </div>
                      </div>

                      <div className="px-6 md:px-8 py-6 bg-white border-t border-neutral-100" data-id="element-11719" data-mp="2kr2kcsdmcx42">
                        <div className="flex items-center justify-between mb-4" data-id="element-11720" data-mp="2koq2ef7v2mr0">
                          <span className="text-sm font-semibold text-[#333333]" data-id="element-11721" data-mp="2fpwqusc4x5th">
                            This will allow Giga to:
                          </span>
                          <span className="text-xs font-mono text-neutral-400" data-id="element-11722" data-mp="2s9aioh5409be">
                            {permModal.scopes.filter((s: any) => s.required || optionalScopes[s.id]).length}
                            /{permModal.scopes.length} permissions
                          </span>
                        </div>

                        <div className="space-y-0 divide-y divide-neutral-100 border-b border-neutral-100 mb-6" data-id="element-11723" data-mp="24eh8j0lwgqlb">
                          {permModal.scopes.map((scope: any) => <label key={scope.id} className={`flex items-start gap-4 py-4 ${scope.required ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-11724" data-mp="2eaav7ctc20gt">
                        
                              <div className="mt-0.5" data-id="element-11725" data-mp="2ia2x56o818d7">
                                <div className={`w-5 h-5 rounded flex items-center justify-center ${scope.required || !!optionalScopes[scope.id] ? 'bg-[#2B211A] border-[#2B211A]' : 'border-neutral-300 border'} ${scope.required ? 'opacity-100' : ''}`} data-id="element-11726" data-mp="3pp7leq196vpr">
                            
                                  {(scope.required || !!optionalScopes[scope.id]) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-11727" data-mp="emsyvvdahjrd" />}
                                </div>
                              </div>
                              <div data-id="element-11728" data-mp="om1rokwf4sc5">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-11729" data-mp="okyy257pobsj">
                                  <span className="text-[15px] font-bold text-[#333333]" data-id="element-11730" data-mp="12cdlqpckd15x">
                                    {scope.label}
                                  </span>
                                  {scope.required && <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 tracking-wide uppercase" data-id="element-11731" data-mp="1cbmitr2q6vwi">
                                      Required
                                    </span>}
                                </div>
                                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-11732" data-mp="3968xx1336tty">
                                  {scope.description}
                                </p>
                              </div>
                            </label>)}
                        </div>

                        <div className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 flex gap-3" data-id="element-11733" data-mp="1evsp45etayg8">
                          <ShieldCheck className="w-5 h-5 text-[#EA580C] shrink-0 mt-0.5" data-id="element-11734" data-mp="1jum4629qyxii" />
                          <p className="text-[13px] text-neutral-600 leading-relaxed" data-id="element-11735" data-mp="15v2w19gxrm67">
                            Giga stores access tokens encrypted at rest and
                            isolated per workspace. Your {permModal.name} data
                            is never shared with other Gigabrain customers or
                            used to train shared models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 md:p-6 bg-white border-t border-neutral-100 flex flex-col md:flex-row items-center justify-between sticky bottom-0 z-10" data-id="element-11736" data-mp="2vbc2z0bucn23">
                      <p className="hidden md:block text-[11px] text-neutral-500 max-w-[180px] leading-relaxed" data-id="element-11737" data-mp="2o19h2gqo6ti">
                        By continuing, you agree to Gigabrain's{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-11738" data-mp="3psh5pk4m0c2b">
                      
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-11739" data-mp="lej2l85hh4fe">
                      
                          Privacy Notice
                        </a>
                        .
                      </p>
                      <div className="flex flex-col-reverse md:flex-row md:items-center gap-3 w-full md:w-auto" data-id="element-11740" data-mp="hdz33xxftoq3">
                        <button onClick={() => {
                    setPermModal(null);
                    setConnectPhase(null);
                    setConnectionsStarted(false);
                  }} className="w-full md:w-auto px-5 py-3.5 md:py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-11741" data-mp="2x53gk875kksr">
                      
                          Cancel
                        </button>
                        <button onClick={() => handleAllowClick(permModal)} className="w-full md:w-auto px-6 py-3.5 md:py-2.5 rounded-xl bg-[#2B211A] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors shadow-sm whitespace-nowrap" data-id="element-11742" data-mp="33k29l2egcfm9">
                      
                          Allow access
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="hidden md:block mt-6 font-mono text-xs text-neutral-400 text-center max-w-sm leading-relaxed" data-id="element-11743" data-mp="2fwkfh07pt5g4">
                    You'll be returned to Gigabrain automatically. This stays in
                    the same tab.
                  </p>
                </div>}

              {connectPhase === 'handing-off' && <div className="flex-1 flex flex-col items-center justify-center w-full" data-id="element-11744" data-mp="3nao5427idkzr">
                  <div className="flex items-center gap-6 mb-6" data-id="element-11745" data-mp="3s8zb21uonbcp">
                    <div className="w-12 h-12 rounded-xl border border-neutral-200 bg-white flex items-center justify-center shadow-sm" data-id="element-11746" data-mp="3s74qionbpl95">
                      <ToolLogo name={permModal.name} icon={permModal.icon} className="w-6 h-6" data-id="element-11747" data-mp="4wokcz3werao" />
                  
                    </div>
                    <Loader2 size={24} className="text-neutral-400 animate-spin" data-id="element-11748" data-mp="34cv3d38mfh3y" />
                
                    <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-11749" data-mp="39awri2kwm6ma">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-11750" data-mp="3ib1nlgkndil5" />
                  
                    </div>
                  </div>
                  <p className="font-mono text-sm text-neutral-500" data-id="element-11751" data-mp="12p66svuzxer9">
                    Handing off to {permModal.name}...
                  </p>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  }
  // Step 8: Now go to Slack
  if (step === 8) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11752" data-mp="f0ls13hj4ydn">
        
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-6" data-id="element-11753" data-mp="29d37vq01gw89">
          <Link to="/" aria-label="Back to Giga home" className="flex items-center gap-2 group w-fit" data-id="element-11754" data-mp="25bzst5ptf483">
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain group-hover:scale-105 transition-transform" data-id="element-11755" data-mp="2cgrafxe8hrkn" />
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-11756" data-mp="27fv7pn5mcbgt" />
            
          </Link>
        </div>

        <div className="flex-1 flex items-center" data-id="element-11757" data-mp="1yi1ckfjlbnv8">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-10" data-id="element-11758" data-mp="1js3ijiyzc3y4">
            {/* Left — call to action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-11759" data-mp="1aqm6cbop0mep">
              
              <h1 className="text-5xl md:text-6xl font-title font-bold text-[#333333] mb-5 tracking-tight" data-id="element-11760" data-mp="w46ys2z1qqgn">
                Giga's waiting in Slack.
              </h1>
              <p className="text-lg text-neutral-600 mb-8 max-w-md leading-relaxed" data-id="element-11761" data-mp="154ln7b7b4lod">
                Open Slack and find Giga under{' '}
                <span className="font-semibold text-[#333333]" data-id="element-11762" data-mp="111gl39r52m09">"Apps"</span>, or
                just <span className="font-semibold text-[#333333]" data-id="element-11763" data-mp="1f1845owiisl4">@Giga</span>{' '}
                them in any channel in {WORKSPACE_NAME}.
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-11764" data-mp="1os2tx38ywabb">
                <button onClick={() => setStep(9)} className="inline-flex items-center justify-center gap-2.5 bg-[#2B211A] text-white px-7 py-4 rounded-xl font-bold text-base hover:bg-black transition-colors shadow-lg shadow-black/20" data-id="element-11765" data-mp="1orjdxs2vnvzx">
                  
                  <SlackIcon size={20} data-id="element-11766" data-mp="1supfwniyj10l" />
                  Message Giga
                </button>
                <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 font-medium hover:text-[#333333] transition-colors" data-id="element-11767" data-mp="qhb1burg4kli">
                  
                  <ArrowLeft size={18} data-id="element-11768" data-mp="vd8ghxqz3kwp" />
                  Back to home
                </Link>
              </div>
            </motion.div>

            {/* Right — Slack window mock */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="hidden lg:block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white" data-id="element-11769" data-mp="14a106zs2pk01">
              
              {/* macOS chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#3F0E40]" data-id="element-11770" data-mp="3kwk1rl78yecr">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-11771" data-mp="3kywpyg3llky9" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-11772" data-mp="3oynnr3rjl1ca" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-11773" data-mp="3hew8dm6kna24" />
                <div className="flex-1 mx-4" data-id="element-11774" data-mp="3cnwcmk96uy17">
                  <div className="flex items-center gap-2 rounded-md bg-black/20 px-3 py-1.5 text-white/60 text-xs" data-id="element-11775" data-mp="3cn4fv63uuu9c">
                    <Search size={12} data-id="element-11776" data-mp="37p2xyoi610zg" />
                    Search
                  </div>
                </div>
              </div>

              <div className="flex h-[420px]" data-id="element-11777" data-mp="4849813s4oen">
                {/* Sidebar */}
                <div className="w-40 shrink-0 bg-[#3F0E40] text-white/80 py-3 flex flex-col" data-id="element-11778" data-mp="46079opfxzh0">
                  <div className="px-4 pb-3 mb-1 border-b border-white/10 flex items-center gap-1.5 font-bold text-white text-sm" data-id="element-11779" data-mp="3vf73x2ittctm">
                    {WORKSPACE_NAME}
                    <ChevronDown size={14} data-id="element-11780" data-mp="3mco3ep2ijcaj" />
                  </div>
                  <div className="px-2 space-y-0.5 text-[13px]" data-id="element-11781" data-mp="2fxdjf1c06x0w">
                    {navItems.map(n => <div key={n.label} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11782" data-mp="2ayk91hs0jm4y">
                      
                        <n.icon size={15} className="opacity-70" data-id="element-11783" data-mp="etuo8fubrsyn" />
                        {n.label}
                      </div>)}
                    <div className="h-px bg-white/10 my-2 mx-2" data-id="element-11784" data-mp="iuobr83uv5r0" />
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11785" data-mp="4ysxxgobckuh">
                      <Hash size={15} className="opacity-70" data-id="element-11786" data-mp="9yfb5q45613a" />
                      Channels
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11787" data-mp="12l0cnjebztkk">
                      <MessageSquare size={15} className="opacity-70" data-id="element-11788" data-mp="nwm36pmet6v1" />
                      Direct messages
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11789" data-mp="stm7auzp7zji">
                      <Grid3x3 size={15} className="opacity-70" data-id="element-11790" data-mp="1giom7v2y7eab" />
                      Apps
                    </div>
                    {/* Active Giga DM */}
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-[#1164A3] text-white mt-0.5" data-id="element-11791" data-mp="360vaxrcsh3to">
                      <span className="flex items-center gap-2.5" data-id="element-11792" data-mp="312k29jy8czd1">
                        <span className="w-4 h-4 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11793" data-mp="3et5mbbzf7fci">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-2.5 h-2.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11794" data-mp="3atwsy88aljhh" />
                          
                        </span>
                        Giga
                      </span>
                      <span className="w-4 h-4 rounded-full bg-[#E01E5A] text-white text-[10px] font-bold flex items-center justify-center" data-id="element-11795" data-mp="2r3kz2gw8hvxf">
                        1
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main column */}
                <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-11796" data-mp="l1ri0a277m9o">
                  {/* DM header + tabs */}
                  <div className="px-4 pt-3 border-b border-neutral-200" data-id="element-11797" data-mp="gxtku4wfolj9">
                    <div className="flex items-center gap-2 mb-3" data-id="element-11798" data-mp="c1cv569l5z42">
                      <span className="w-6 h-6 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11799" data-mp="71gp71hbvxlv">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11800" data-mp="14ophpgfqy6xd" />
                        
                      </span>
                      <span className="font-bold text-[#333333] text-sm" data-id="element-11801" data-mp="pz1da6c9qs5f">
                        Giga
                      </span>
                      <ChevronDown size={14} className="text-neutral-400" data-id="element-11802" data-mp="1nhjmjhjnoinv" />
                    </div>
                    <div className="flex items-center gap-5 text-sm" data-id="element-11803" data-mp="1imnjzngsmcwi">
                      <span className="pb-2 text-neutral-500" data-id="element-11804" data-mp="1q16hiy0lkv2j">Home</span>
                      <span className="pb-2 -mb-px border-b-2 border-[#333333] font-semibold text-[#333333]" data-id="element-11805" data-mp="1uy6duh7najgg">
                        Messages
                      </span>
                      <span className="pb-2 text-neutral-500" data-id="element-11806" data-mp="24x5o24826n5k">About</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto px-4 py-4" data-id="element-11807" data-mp="28y7q0std1db8">
                    <div className="text-center text-[11px] text-neutral-400 mb-4" data-id="element-11808" data-mp="2dx27nrflcvtr">
                      This is the very beginning of your direct message history
                      with <span className="text-[#1164A3]" data-id="element-11809" data-mp="2nnneg25f56nr">@Giga</span>
                    </div>
                    <div className="flex items-start gap-2.5" data-id="element-11810" data-mp="2npho2kb8pccj">
                      <span className="w-9 h-9 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-11811" data-mp="2sob4akhwvhge">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11812" data-mp="2wol8psq21zt5" />
                        
                      </span>
                      <div className="min-w-0" data-id="element-11813" data-mp="11l6t84ag9aa5">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-11814" data-mp="11kdhsq0jpwlo">
                          <span className="font-bold text-sm text-[#333333]" data-id="element-11815" data-mp="1g99z7dohslyy">
                            Giga
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11816" data-mp="1baqcvc5zl862">
                            App
                          </span>
                          <span className="text-[11px] text-neutral-400 hover:underline cursor-pointer" data-id="element-11817" data-mp="3r1uln94rbg2e">
                            9:16 AM
                          </span>
                        </div>
                        <p className="text-sm text-[#333333] leading-snug mb-2" data-id="element-11818" data-mp="3cwu91dam8xcz">
                          Hi 👋, great to meet you. I'm Giga, your new AI
                          coworker. Here are two ways to work with me:
                        </p>
                        <p className="text-sm text-[#333333] leading-snug mb-1.5" data-id="element-11819" data-mp="336iw518yfdty">
                          💬 <span className="font-bold" data-id="element-11820" data-mp="384aykp786ice">DM me here</span> —
                          just message me like a coworker. Research, analysis,
                          reports, automation.
                        </p>
                        <p className="text-sm text-[#333333] leading-snug" data-id="element-11821" data-mp="2y5li90uz95y9">
                          <span className="text-[#1164A3] font-medium" data-id="element-11822" data-mp="2k9hplbzr55nw">
                            @Giga
                          </span>{' '}
                          <span className="font-bold" data-id="element-11823" data-mp="igthopc5xu1s">in any channel</span> —
                          mention me in context and I'll jump in with the full
                          thread as background.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Slack mock (Mobile) */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="lg:hidden rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white flex flex-col w-full max-w-md mx-auto" data-id="element-11824" data-mp="29ju2r7s2wvzb">
              
              {/* Mobile Slack Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-3 flex items-center gap-3" data-id="element-11825" data-mp="1qrrkxt3pb9nh">
                <div className="w-9 h-9 rounded bg-white/20 flex items-center justify-center shrink-0" data-id="element-11826" data-mp="1lt6fnf9ldzs6">
                  <span className="font-bold text-base" data-id="element-11827" data-mp="1hv8q2bbwtbo9">
                    {WORKSPACE_INITIAL}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-11828" data-mp="3ogmw431i3kpv">
                  <div className="text-xs font-medium text-white/70 truncate" data-id="element-11829" data-mp="3ok0yhba5ztxb">
                    {WORKSPACE_NAME}
                  </div>
                  <div className="font-bold text-[15px] truncate leading-tight" data-id="element-11830" data-mp="1cqiz5f0o9wo">
                    Giga
                  </div>
                </div>
                <Search size={20} className="text-white/80" data-id="element-11831" data-mp="69z2b9bwijzi" />
              </div>

              {/* Mobile Messages Area */}
              <div className="flex-1 bg-white p-5" data-id="element-11832" data-mp="39sinjrw5wpkt">
                <div className="text-center text-[11px] text-neutral-400 mb-5" data-id="element-11833" data-mp="39tbjdxtl1uo6">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3]" data-id="element-11834" data-mp="3jiv4la4rq0m7">@Giga</span>
                </div>
                <div className="flex items-start gap-3" data-id="element-11835" data-mp="2m3hcrxzux2cd">
                  <span className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-11836" data-mp="2m2xsiamqi1v6">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11837" data-mp="2qzxw8ew42sj4" />
                    
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-11838" data-mp="2yity8a2dlhm">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-11839" data-mp="2z2abkk2g1tj">
                      <span className="font-bold text-[15px] text-[#333333]" data-id="element-11840" data-mp="3bdkbr3fi4j46">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11841" data-mp="36gtzkr6ti7o1">
                        App
                      </span>
                      <span className="text-xs text-neutral-400" data-id="element-11842" data-mp="3fia62nuihpmz">9:16 AM</span>
                    </div>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2.5" data-id="element-11843" data-mp="zqm9itjetz4p">
                      Hi 👋, great to meet you. I'm Giga, your new AI coworker.
                      Here are two ways to work with me:
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2" data-id="element-11844" data-mp="179df952xchbt">
                      💬 <span className="font-bold" data-id="element-11845" data-mp="t9cgbv67zjyz">DM me here</span> — just
                      message me like a coworker. Research, analysis, reports,
                      automation.
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug" data-id="element-11846" data-mp="1ly9mizx5pqbd">
                      <span className="text-[#1164A3] font-medium" data-id="element-11847" data-mp="1qw1mmh7ygiqn">@Giga</span>{' '}
                      <span className="font-bold" data-id="element-11848" data-mp="1c7exjnsj0cm8">in any channel</span> —
                      mention me in context and I'll jump in with the full
                      thread as background.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>;
  }
  if (step === 9) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="h-screen w-full bg-white font-body flex flex-col overflow-hidden" data-id="element-11849" data-mp="3vzcp7c3ajmwo">
        
        {/* macOS chrome & Slack header */}
        <div className="flex items-center gap-4 px-4 py-2 bg-[#3F0E40] border-b border-white/10 shrink-0" data-id="element-11850" data-mp="si4a2zzy715n">
          <div className="flex items-center gap-2 w-16" data-id="element-11851" data-mp="nkubouukzf5o">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-11852" data-mp="jiha8imva3ep" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-11853" data-mp="jktxnbbe95z5" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-11854" data-mp="eiusbvw3fyzk" />
          </div>
          <div className="flex-1 flex justify-center" data-id="element-11855" data-mp="34987jl6x1dz5">
            <div className="flex items-center gap-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-white/80 text-sm w-full max-w-2xl cursor-text" data-id="element-11856" data-mp="346lq8tu83k3q">
              <Search size={14} data-id="element-11857" data-mp="1etnsbu3j0e0n" />
              Search {WORKSPACE_NAME}
            </div>
          </div>
          <div className="w-16 flex justify-end" data-id="element-11858" data-mp="1jr5qcpg098tv">
            <div className="w-7 h-7 rounded bg-white/20" data-id="element-11859" data-mp="1nrzf1l19urnt" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0" data-id="element-11860" data-mp="1sizdwvtybjsy">
          {/* Sidebar */}
          <div className="w-64 shrink-0 bg-[#3F0E40] text-white/80 py-4 flex flex-col overflow-y-auto" data-id="element-11861" data-mp="1sl2bq21cih88">
            <div className="px-4 pb-4 mb-2 border-b border-white/10 flex items-center justify-between font-bold text-white text-lg" data-id="element-11862" data-mp="1xkomm4tpbldv">
              <span className="flex items-center gap-1.5" data-id="element-11863" data-mp="27g8iir89mk2m">
                {WORKSPACE_NAME}
                <ChevronDown size={16} data-id="element-11864" data-mp="2billx8zji5w8" />
              </span>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-11865" data-mp="2bfzed9cnfqe0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11866" data-mp="d4k36ny4gfhp">
                  
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-11867" data-mp="355kp1mq7py76"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-11868" data-mp="3a3m9aazdk3k8"></path>
                </svg>
              </div>
            </div>
            <div className="px-3 space-y-0.5 text-[15px]" data-id="element-11869" data-mp="3juqptf4p7wh6">
              {navItems.map(n => <div key={n.label} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer" data-id="element-11870" data-mp="3jwtdhsidpdiq">
                
                  <n.icon size={18} className="opacity-70" data-id="element-11871" data-mp="2r74bbbtxn7fj" />
                  {n.label}
                </div>)}
              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-11872" data-mp="2w3us61dka720" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-11873" data-mp="3079ibkv7s3t6">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-11874" data-mp="bfyjl2jk73gs">
                  <ChevronDown size={14} data-id="element-11875" data-mp="ffqw1qc2ptrf" />
                  Channels
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-11876" data-mp="1zxj9zwsxhv92">
                <Hash size={16} className="opacity-70" data-id="element-11877" data-mp="1vzc1tqcj629h" />
                general
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-11878" data-mp="1r46845cvzjau">
                <Hash size={16} className="opacity-70" data-id="element-11879" data-mp="1h8utro5e4e3m" />
                engineering
              </div>

              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-11880" data-mp="1c8yycdarduzo" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-11881" data-mp="1cbl4c8i042fs">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-11882" data-mp="189hqjd7v0lrc">
                  <ChevronDown size={14} data-id="element-11883" data-mp="32lo24d4o14k6" />
                  Direct messages
                </span>
              </div>

              {/* Active Giga DM */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#1164A3] text-white mt-1 cursor-pointer" data-id="element-11884" data-mp="2xn4hcg1t5yte">
                <span className="flex items-center gap-3" data-id="element-11885" data-mp="3lhwnr3w856m8">
                  <span className="relative w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11886" data-mp="3ljhiatijgpk3">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11887" data-mp="37scqgdlex1kb" />
                    
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#1164A3]" data-id="element-11888" data-mp="8alj51kyxp1m" />
                  </span>
                  Giga
                </span>
              </div>

              {[{
              name: 'Priya Patel',
              initial: 'P',
              color: 'bg-[#4A9E8E]',
              online: true
            }, {
              name: 'Marcus Lee',
              initial: 'M',
              color: 'bg-[#9B6DD6]',
              online: true
            }, {
              name: 'Jordan Kim',
              initial: 'J',
              color: 'bg-[#E0727F]',
              online: false
            }, {
              name: 'Elena Rodriguez',
              initial: 'E',
              color: 'bg-[#D9A441]',
              online: false
            }, {
              name: 'Sarah (you)',
              initial: 'S',
              color: 'bg-[#4F84D6]',
              online: true
            }].map(person => <div key={person.name} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-1" data-id="element-11889" data-mp="1osmywj9yw84p">
                
                  <span className={`relative w-5 h-5 rounded ${person.color} flex items-center justify-center text-xs font-bold text-white shrink-0`} data-id="element-11890" data-mp="1xsifj3makvc4">
                  
                    {person.initial}
                    {person.online ? <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#3F0E40]" data-id="element-11891" data-mp="154mlk6o3xhj1" /> : <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3F0E40] border-2 border-white/50" data-id="element-11892" data-mp="1irmnegbijbuh" />}
                  </span>
                  {person.name}
                </div>)}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-11893" data-mp="1xfzmvn9t7ctq">
            {/* Header */}
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0" data-id="element-11894" data-mp="21hbchssony1x">
              <div className="flex items-center gap-3" data-id="element-11895" data-mp="26fcyalhhubh3">
                <span className="w-8 h-8 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11896" data-mp="26i8spzu4ui0s">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11897" data-mp="2bd65nhuj9biy" />
                  
                </span>
                <div className="flex flex-col" data-id="element-11898" data-mp="m25ii3kctttu">
                  <div className="flex items-center gap-2" data-id="element-11899" data-mp="2zbm2ux30ermt">
                    <span className="font-bold text-[#333333] text-[15px]" data-id="element-11900" data-mp="2uk44mgsdf55j">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11901" data-mp="2qjbu7k7riu7o">
                      App
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#2BAC76]" data-id="element-11902" data-mp="3i8wrzouxg4oj" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-neutral-500" data-id="element-11903" data-mp="3e60da98icspv">
                <span className="font-semibold text-sm pb-1 border-b-2 border-[#333333] text-[#333333] cursor-pointer" data-id="element-11904" data-mp="396xaywjwx0ll">
                  Messages
                </span>
                <span className="font-semibold text-sm pb-1 border-b-2 border-transparent hover:text-[#333333] cursor-pointer" data-id="element-11905" data-mp="1xumof7vri0pl">
                  About
                </span>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-5 flex flex-col" data-id="element-11906" data-mp="1u9ulrx2rlahf">
              {/* Intro Block */}
              <div className="mb-8 mt-4" data-id="element-11907" data-mp="1lazhiqja6agr">
                <div className="flex items-center gap-4 mb-4" data-id="element-11908" data-mp="1l9xxvxo78fnf">
                  <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-sm" data-id="element-11909" data-mp="1q9u0ptl5u11e">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11910" data-mp="1bkxttzuuau2d" />
                    
                  </div>
                  <div data-id="element-11911" data-mp="16n4o17xg822m">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11912" data-mp="16mctm0fl8adx">
                      <span className="font-bold text-2xl text-[#333333]" data-id="element-11913" data-mp="snd30n8illf4">
                        Giga
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11914" data-mp="xkmzgmubmxcg">
                        App
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2BAC76]" data-id="element-11915" data-mp="nxsuqzmyttwe" />
                    </div>
                  </div>
                </div>
                <p className="text-[15px] text-[#333333] mb-2" data-id="element-11916" data-mp="ixwpyy6g1yfs">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-11917" data-mp="rze4ic47ds2d">
                    @Giga
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-[#1164A3] text-[15px] hover:underline cursor-pointer w-fit" data-id="element-11918" data-mp="16mz784x61p9r">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11919" data-mp="11raf2mnh7anh">
                    
                    <circle cx="12" cy="12" r="10" data-id="element-11920" data-mp="1boyazfwtio78"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-11921" data-mp="2i2uot545xn5b"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-11922" data-mp="2n1fpt6r8oaut"></line>
                  </svg>
                  How does Giga work?
                </div>
              </div>

              {/* Date Divider */}
              <div className="relative flex items-center justify-center mb-6" data-id="element-11923" data-mp="2w246yabxirbx">
                <div className="absolute inset-0 flex items-center" data-id="element-11924" data-mp="2vzzyrg6zz65u">
                  <div className="w-full border-t border-neutral-200" data-id="element-11925" data-mp="1zdelyb509ilq"></div>
                </div>
                <div className="relative bg-white px-4" data-id="element-11926" data-mp="24bpsod007czq">
                  <span className="text-[13px] font-bold text-[#333333] border border-neutral-200 rounded-full px-4 py-1 shadow-sm" data-id="element-11927" data-mp="28crw760yeb7i">
                    Today <ChevronDown size={12} className="inline ml-1" data-id="element-11928" data-mp="2d41hxvjdsue4" />
                  </span>
                </div>
              </div>

              {/* Giga Onboarding Message */}
              <div className="flex items-start gap-3 hover:bg-neutral-50 -mx-5 px-5 py-2 transition-colors" data-id="element-11929" data-mp="1km5sfqxzfd3b">
                <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5" data-id="element-11930" data-mp="1d24uv7y7kiy0">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11931" data-mp="184v0ddvyph3b" />
                  
                </div>
                <div className="min-w-0 flex-1" data-id="element-11932" data-mp="u5vbc54jf06m">
                  <div className="flex items-center gap-2 mb-1" data-id="element-11933" data-mp="pdbyjadoc92v">
                    <span className="font-bold text-[15px] text-[#333333]" data-id="element-11934" data-mp="pc0quzh3yh6h">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11935" data-mp="gdp2l43prvh7">
                      App
                    </span>
                    <span className="text-xs text-neutral-500 hover:underline cursor-pointer" data-id="element-11936" data-mp="6g0xbk8hf5ks">
                      3:27 PM
                    </span>
                  </div>

                  <div className="text-[15px] text-[#333333] leading-relaxed space-y-3" data-id="element-11937" data-mp="37hifg6j3stz7">
                    <p data-id="element-11938" data-mp="3lgi2xauvr2ts">
                      Hi 👋, great to meet you. I am Giga, your new AI coworker.
                      Here are three ways to work with me:
                    </p>

                    <div className="space-y-1.5" data-id="element-11939" data-mp="2ss2sz2qlvapb">
                      <p data-id="element-11940" data-mp="2ssuoyeo47ag4">
                        💬 <span className="font-bold" data-id="element-11941" data-mp="2sq05oqgu7voa">DM me here</span> — just
                        message me like a coworker. Research, analysis, reports,
                        automation — anything.
                      </p>
                      <p data-id="element-11942" data-mp="2xrfoebvjlq3i">
                        📣{' '}
                        <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium cursor-pointer" data-id="element-11943" data-mp="2xpuv0pkysd78">
                          @Giga
                        </span>{' '}
                        <span className="font-bold" data-id="element-11944" data-mp="251z0nrivaxdm">in any channel</span> —
                        mention me in context and I'll jump in with the full
                        thread as background.
                      </p>
                      <p data-id="element-11945" data-mp="pd98v84gn325">
                        🔌{' '}
                        <span className="font-bold" data-id="element-11946" data-mp="bksy1l5bi0qo">
                          I connect to 3000+ tools
                        </span>{' '}
                        — no tools are connected yet. Need GitHub, Stripe,
                        HubSpot, Google Ads, or anything else? Just tell me and
                        I'll figure out access.
                      </p>
                      <p data-id="element-11947" data-mp="uaj454ej68xw">
                        🔍{' '}
                        <span className="font-bold" data-id="element-11948" data-mp="u9zmlosslmke">
                          I'm now reading through the workspace history
                        </span>{' '}
                        — I'll propose ideas for how I can help over the next
                        hours.
                      </p>
                    </div>

                    <p data-id="element-11949" data-mp="3jx8ewziddh3z">
                      Try one of these now — just copy, paste, and hit send:
                    </p>

                    <div className="space-y-4" data-id="element-11950" data-mp="1kjro6fi4rk6u">
                      <div data-id="element-11951" data-mp="1kl1olbcgh4gn">
                        <p className="italic text-neutral-600 mb-1" data-id="element-11952" data-mp="1ftjosqm659x9">
                          1. In a channel:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-11953" data-mp="1bqwsodigt2u9">
                          <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium" data-id="element-11954" data-mp="16teikjspohko">
                            @Giga
                          </span>{' '}
                          summarize what's been discussed in this channel this
                          week
                        </div>
                      </div>

                      <div data-id="element-11955" data-mp="suwweqigfd4v">
                        <p className="italic text-neutral-600 mb-1" data-id="element-11956" data-mp="2r8q65uqveidh">
                          2. Or right here:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-11957" data-mp="2n8690r6arerc">
                          Research the top 5 trends in our industry and put
                          together a brief report
                        </div>
                      </div>
                    </div>

                    <p data-id="element-11958" data-mp="iox1g9v21r0v">
                      Or just tell me what you need — I'll take it from there 🚀
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="px-5 pb-6 pt-2 shrink-0" data-id="element-11959" data-mp="3m7yqca4b5d44">
              <div className="border border-neutral-400 rounded-xl overflow-hidden focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500 transition-all shadow-sm" data-id="element-11960" data-mp="3r6072oe9h0cf">
                {/* Toolbar */}
                <div className="bg-neutral-50 border-b border-neutral-200 px-2 py-1.5 flex items-center gap-1" data-id="element-11961" data-mp="41u360ywrech">
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11962" data-mp="1aifkp6s9s54s">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11963" data-mp="1fdl5u3dulfrt">
                      
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-11964" data-mp="190qpogmfduaq"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-11965" data-mp="1n003j9n5scbj"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11966" data-mp="1iw23mwq0exhf">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11967" data-mp="1wvjzfw1q3b0a">
                      
                      <line x1="19" y1="4" x2="10" y2="4" data-id="element-11968" data-mp="26lmqvrt74gwi"></line>
                      <line x1="14" y1="20" x2="5" y2="20" data-id="element-11969" data-mp="21nbfhcn95yc6"></line>
                      <line x1="15" y1="4" x2="9" y2="20" data-id="element-11970" data-mp="21pxv7zkark5h"></line>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11971" data-mp="2aojexy1i957u">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11972" data-mp="2amosugehz726">
                      
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" data-id="element-11973" data-mp="2z71vnwkf802c"></path>
                      <line x1="4" y1="21" x2="20" y2="21" data-id="element-11974" data-mp="2l93gwq26u1mb"></line>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-neutral-300 mx-1" data-id="element-11975" data-mp="2pdb8cv015vo8" />
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11976" data-mp="2bj1us3teata1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11977" data-mp="2gblhprdz30ri">
                      
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" data-id="element-11978" data-mp="26jz27iulqfiy"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" data-id="element-11979" data-mp="1skpkgeqqzdcv"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11980" data-mp="1xm58mfo81zzh">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11981" data-mp="35wznv57y532">
                      
                      <line x1="8" y1="6" x2="21" y2="6" data-id="element-11982" data-mp="3pjoi2fwopkiq"></line>
                      <line x1="8" y1="12" x2="21" y2="12" data-id="element-11983" data-mp="3limhnvr172f6"></line>
                      <line x1="8" y1="18" x2="21" y2="18" data-id="element-11984" data-mp="m2prro2ngzvt"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-11985" data-mp="m1wvjh1bhsrx"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-11986" data-mp="h3d4j27v9qsp"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-11987" data-mp="d1zzgucllrmu"></line>
                    </svg>
                  </button>
                </div>

                {/* Input Area */}
                <div className="p-3 min-h-[80px] bg-white" data-id="element-11988" data-mp="8516if4o7qq8">
                  <p className="text-neutral-400 text-[15px]" data-id="element-11989" data-mp="2xta6wrg22mar">Message Giga</p>
                </div>

                {/* Bottom Actions */}
                <div className="bg-white px-2 pb-2 flex items-center justify-between" data-id="element-11990" data-mp="2sv8rqhlr374k">
                  <div className="flex items-center gap-1" data-id="element-11991" data-mp="18cy6mjgp3708">
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-11992" data-mp="1d9eynor0m5il">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11993" data-mp="1hbjki1b81xuo">
                        
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-11994" data-mp="1r2ea9fnoffkv"></line>
                        <line x1="5" y1="12" x2="19" y2="12" data-id="element-11995" data-mp="1r3z7xe5afemy"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-11996" data-mp="20z98ryjy9fz5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11997" data-mp="251w4iajqrl1m">
                        
                        <circle cx="12" cy="12" r="10" data-id="element-11998" data-mp="an37c737mm7k"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" data-id="element-11999" data-mp="apfoii158sjk"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9" data-id="element-12000" data-mp="3nvgv1irb5ncy"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9" data-id="element-12001" data-mp="3ixnm4hymef8z"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12002" data-mp="id2ntgf93zn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12003" data-mp="3so9x1yve6hlc">
                        
                        <circle cx="12" cy="12" r="4" data-id="element-12004" data-mp="2zzbfb2v6lhgk"></circle>
                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" data-id="element-12005" data-mp="300w5kgagqe8m"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-12006" data-mp="38xohgw5bn91e">
                    <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded" data-id="element-12007" data-mp="t7ka5hxut3x1">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12008" data-mp="o9ikge5xa6cl">
                        
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-12009" data-mp="10rcqjwuh26lc"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-12010" data-mp="10pi7ko2rgojw"></polygon>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 10: Welcome back (signed out / sign in to another workspace)
  if (step === 10) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center px-6 overflow-hidden" data-id="element-12011" data-mp="1kekbkdi4ea8e">
        
        {/* Brand cream surface with a soft warm bloom so it stays consistent
                                                                  with the rest of the auth flow (the "Try Giga free" screen). The
                                                                  orange primary CTA reads boldly against the cream. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full bg-[#EA580C]/[0.07] blur-[160px]" data-id="element-12012" data-mp="1n1yrgrhzz7az" />
        
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/[0.05] blur-[150px]" data-id="element-12013" data-mp="1x05z2jqecd0b" />
        

        {/* Return home */}
        <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12014" data-mp="14bh0arctcjrq">
          
          <ArrowLeft size={16} data-id="element-12015" data-mp="1i4gjb3bt0w41" />
          Back to home
        </Link>

        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center text-center" data-id="element-12016" data-mp="1dacbyiymmm2f">
          {/* Logo */}
          <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-[#F97316]/20 mb-8" data-id="element-12017" data-mp="lkplvbdyy36h">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-9 h-9 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12018" data-mp="bfretzsooy1m" />
            
          </div>

          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-12019" data-mp="6hg31jitwdgr">
            {signedOut ? 'Sign in to Giga' : 'Welcome back'}
          </h1>

          <p className="text-sm text-neutral-500 mb-10" data-id="element-12020" data-mp="u5fqoakufnr4">
            {signedOut ? "You've been signed out of all workspaces." : 'Pick up right where you left off.'}
          </p>

          <div className="w-full space-y-3" data-id="element-12021" data-mp="kb73m2el0n0c">
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12022" data-mp="p7o0pm3rebpk">
              
              <SlackIcon size={20} data-id="element-12023" data-mp="39wbsewa69kk6" />
              <span className="font-semibold text-white" data-id="element-12024" data-mp="1gfft1f6ogrkv">
                Continue with Slack
              </span>
            </button>
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-[#EAE4D9] bg-white transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-12025" data-mp="1bhxi5j93o2a8">
              <GoogleIcon size={20} data-id="element-12026" data-mp="ws85aqrna9u4" />
              <span className="font-semibold text-[#2B211A]" data-id="element-12027" data-mp="wt9qvp6aeeq3">
                Continue with Google
              </span>
            </button>
            {teamsWaitlistDone ? <div className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-[#EA580C]/[0.08] border border-[#EA580C]/20 text-[#2B211A]" data-id="element-12028" data-mp="nqie9m1odcwv">
                <Check size={18} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12029" data-mp="iua2mimos1ug" />
                <span className="text-sm font-medium" data-id="element-12030" data-mp="2i49uj4eiev1a">
                  You're on the Microsoft Teams waitlist — we'll email you when
                  it's ready.
                </span>
              </div> : teamsWaitlistOpen ? <form onSubmit={submitTeamsWaitlist} className="w-full flex flex-col sm:flex-row gap-2" data-id="element-12031" data-mp="2knn131q218cd">
              
                <label htmlFor="teams-waitlist-email" className="sr-only" data-id="element-12032" data-mp="2ufkducwpo8d7">
                  Work email for the Microsoft Teams waitlist
                </label>
                <input id="teams-waitlist-email" type="email" required autoFocus value={teamsWaitlistEmail} onChange={e => setTeamsWaitlistEmail(e.target.value)} placeholder="you@company.com" className="flex-1 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30 transition-all" data-id="element-12033" data-mp="2zdlvctehnzmk" />
              
                <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12034" data-mp="3n3ycmuvmj8pt">
                
                  Notify me
                  <ArrowRight size={18} data-id="element-12035" data-mp="3ponw6cmf1bh8" />
                </button>
              </form> : <button onClick={() => setTeamsWaitlistOpen(true)} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] shadow-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-12036" data-mp="3lo41rgt88lys">
              
                <TeamsIcon size={20} data-id="element-12037" data-mp="cgsxxwc5dh2r" />
                <span className="font-medium" data-id="element-12038" data-mp="cfrm3pnb4qjz">
                  Join Microsoft Teams waitlist
                </span>
              </button>}
          </div>

          {/* Your Workspaces Section — hidden once the user signs out of all
                                                              workspaces, leaving a clean sign-in with just the providers above. */}
          {!signedOut && <div className="w-full mt-10" data-id="element-12039" data-mp="w6d40z0uob73">
              <div className="flex items-center gap-4 mb-6" data-id="element-12040" data-mp="r6quazk8dd31">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12041" data-mp="r8le2ajrmz5j" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-12042" data-mp="1asagwfjhg0zc">
                  Your Workspaces
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12043" data-mp="1ert2ldj4og38" />
              </div>

              <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-between p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-white transition-all group text-left bg-white shadow-sm" data-id="element-12044" data-mp="10u4hq5p298y6">
              
                <div className="flex items-center gap-4" data-id="element-12045" data-mp="vw307p7a9hot">
                  <div className="w-10 h-10 rounded-lg bg-[#2B211A] border border-black/10 text-white flex items-center justify-center font-bold text-sm" data-id="element-12046" data-mp="i44vdqklu7yg">
                    FT
                  </div>
                  <div className="flex flex-col" data-id="element-12047" data-mp="85yy5m9qtpzx">
                    <span className="font-semibold text-[#2B211A]" data-id="element-12048" data-mp="868k8yro3d0c">
                      Faith Test
                    </span>
                    <span className="text-sm text-neutral-500" data-id="element-12049" data-mp="d6mkhun11int">
                      alqahtani.faith03@gmail.com
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-12050" data-mp="3lcrjwn9765xf">
                  <Check size={20} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12051" data-mp="3ggt2jjmado6i" />
                
                  <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-12052" data-mp="3bhpwpwyhfdxh" />
                
                </div>
              </button>

              <button onClick={() => setSignedOut(true)} className="mt-6 text-sm text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12053" data-mp="7zeqpcxtgpqj">
              
                Sign out of all workspaces
              </button>
            </div>}

          <p className="mt-10 text-sm text-neutral-500" data-id="element-12054" data-mp="3u6d1sfgns3b2">
            No account?{' '}
            <button onClick={() => {
            // Send the user to the free-trial start ("Try Giga free")
            // screen — step 1 is the default signup entry.
            setSignedOut(false);
            setStep(1);
            navigate('/app-screen-mockups/signup');
          }} className="font-bold text-[#2B211A] underline hover:text-[#EA580C] transition-colors" data-id="element-12055" data-mp="2ns58oznh1qhc">
              
              Sign up
            </button>
          </p>
        </div>

        <p className="relative z-10 max-w-md text-center text-xs text-neutral-400 pb-8" data-id="element-12056" data-mp="1cyl21cun76sw">
          By logging in, you agree to the Giga{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12057" data-mp="1hx629c9w9qhc">
            
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12058" data-mp="1qyvwepv5v3it">
            
            Terms of Service
          </a>
          .
        </p>
      </motion.div>;
  }
  // Step 11: Trial Paywall
  if (step === 11) {
    const plans = [{
      id: 'plus',
      name: 'Plus',
      desc: 'The ultimate AI work assistant.',
      price: '$49.99/mo'
    }, {
      id: 'pro',
      name: 'Pro',
      desc: 'More usage for power users.',
      price: '$99.99/mo',
      popular: true
    }];
    const selectedPrice = plans.find(p => p.id === plan)?.price.split('/')[0] || '$49.99';
    const futureDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const selectedPlan = plans.find(p => p.id === plan) || plans[0];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen w-full bg-white font-body" data-id="element-12059" data-mp="1khuc78az39j0">
        
        {/* Real Stripe Checkout anatomy: full-height split, dark merchant
         summary rail on the left, white payment pane on the right. */}
        <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2" data-id="element-12060" data-mp="1pfvwfwk4xew2">
          {/* ── Left: dark order summary ─────────────────────────────────── */}
          <aside className="flex flex-col bg-[#1B130D] px-5 py-5 text-white sm:px-10 lg:py-16 lg:pl-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))] lg:pr-16" data-id="element-12061" data-mp="30xf35ywxbzzd">
            <div className="flex flex-1 flex-col" data-id="element-12062" data-mp="38ihmphf95116">
              {/* back + merchant (logo in a white circle, exactly like Stripe) */}
              <div className="flex items-center gap-3" data-id="element-12063" data-mp="2zg8cksnwb9ob">
                <button type="button"
              // In the signup flow the paywall is reached from the tool
              // connections screen (step 7), not the sign-in screen
              // (step 10) — back should return to connections.
              onClick={() => setStep(7)} aria-label="Back" className="text-white/50 transition-colors hover:text-white" data-id="element-12064" data-mp="2zhjcuii4tzao">
                  
                  <ArrowLeft size={18} data-id="element-12065" data-mp="3ie2j901jx93j" />
                </button>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white" data-id="element-12066" data-mp="3meurpr2ln9yr">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-4 w-4 object-contain" data-id="element-12067" data-mp="dz1naaljloy2" />
                  
                </span>
                <span className="text-[15px] font-medium text-white" data-id="element-12068" data-mp="1o96hbm3v9faf">Giga</span>
              </div>

              {/* headline price */}
              <div className="mt-6 lg:mt-12" data-id="element-12069" data-mp="1t1fzuh2njd2o">
                <p className="text-[14px] text-white/60 lg:text-[15px]" data-id="element-12070" data-mp="1xz83u9gkmpn8">
                  Subscribe to Giga {selectedPlan.name}
                </p>
                <div className="mt-1 flex items-start gap-2" data-id="element-12071" data-mp="11aa8ap0zynp0">
                  <span className="text-[32px] font-bold leading-none tracking-tight lg:text-[44px]" data-id="element-12072" data-mp="15aaks44jcotf">
                    {selectedPrice}
                  </span>
                  <span className="mt-0.5 text-[13px] leading-tight text-white/60 lg:mt-1 lg:text-[15px]" data-id="element-12073" data-mp="1abq8y520fbg1">
                    per
                    <br data-id="element-12074" data-mp="2lp2f4ml8s65d" />
                    month
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-white/50 lg:mt-3 lg:text-[14px]" data-id="element-12075" data-mp="2qnn4ts2vb7fk">
                  7-day free trial · US$0.00 due today
                </p>

                {/* plan segmented control */}
                <div className="mt-5 inline-flex items-center rounded-lg border border-white/15 p-0.5 text-[13px]" data-id="element-12076" data-mp="a0xaiba874h2">
                  {plans.map(p => <button key={p.id} onClick={() => setPlan(p.id)} className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 font-medium transition-colors ${plan === p.id ? 'bg-white text-[#1B130D]' : 'text-white/60 hover:text-white'}`} data-id="element-12077" data-mp="59fiicq6ke88">
                    
                      {p.name}
                      {p.popular && <span className={`rounded px-1 text-[10px] font-bold uppercase ${plan === p.id ? 'bg-[#EA580C] text-white' : 'text-[#EA580C]'}`} data-id="element-12078" data-mp="3nf20gzqxwoft">
                      
                          Popular
                        </span>}
                    </button>)}
                </div>
              </div>

              {/* mobile-only details disclosure */}
              <button type="button" onClick={() => setPayDetailsOpen(o => !o)} aria-expanded={payDetailsOpen} className="mt-4 flex items-center gap-1.5 self-start text-[13px] font-medium text-white/60 transition-colors hover:text-white lg:hidden" data-id="element-12079" data-mp="1bqkm9bg86fkd">
                
                {payDetailsOpen ? 'Hide details' : 'View details'}
                <ChevronDown size={15} className={`transition-transform ${payDetailsOpen ? 'rotate-180' : ''}`} data-id="element-12080" data-mp="2wafuwf96gyd2" />
                
              </button>

              {/* breakdown */}
              <div className={`${payDetailsOpen ? 'block' : 'hidden'} lg:block`} data-id="element-12081" data-mp="3oytv91nqo6rs">
                <div className="mt-6 flex items-start justify-between gap-4 lg:mt-12" data-id="element-12082" data-mp="3twvcri5iny15">
                  <div data-id="element-12083" data-mp="3fxdor501oosu">
                    <p className="text-[15px] font-medium text-white" data-id="element-12084" data-mp="3fw2jt1uv5b05">
                      Giga {selectedPlan.name}
                    </p>
                    <p className="mt-0.5 text-[13px] text-white/50" data-id="element-12085" data-mp="3k0aav5osn31j">
                      Billed monthly
                    </p>
                  </div>
                  <p className="text-[15px] font-medium text-white" data-id="element-12086" data-mp="28k23mqyf33jj">
                    {selectedPrice}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6" data-id="element-12087" data-mp="1w0onw0ipvqhz">
                  <span className="text-[15px] font-medium text-white" data-id="element-12088" data-mp="2axxg0wfugyar">
                    Subtotal
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12089" data-mp="25ykviicg43bc">
                    {selectedPrice}
                  </span>
                </div>

                <div className="mt-5" data-id="element-12090" data-mp="1i75mk6q5ynr4">
                  <button type="button" className="rounded-lg bg-white/[0.07] px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/10" data-id="element-12091" data-mp="1dayhsh9i7iar">
                    
                    Add promotion code
                  </button>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-5" data-id="element-12092" data-mp="zisehvrg95sq">
                  <span className="text-[15px] font-medium text-white" data-id="element-12093" data-mp="ujpamen7h5mc">
                    Total due today
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12094" data-mp="3typzyb0z5ieq">
                    US$0.00
                  </span>
                </div>
              </div>

              {/* footer pinned bottom — desktop only */}
              <div className="mt-auto hidden items-center gap-4 pt-14 text-[12px] text-white/40 lg:flex" data-id="element-12095" data-mp="6rfmsbtauah9">
                <span className="flex items-center gap-1.5" data-id="element-12096" data-mp="bl0b1c53bq39">
                  Powered by{' '}
                  <span className="font-bold text-white/70" data-id="element-12097" data-mp="giilj6yraddd">stripe</span>
                </span>
                <span className="h-3 w-px bg-white/20" data-id="element-12098" data-mp="lgbsi27vvbep" />
                <button type="button" className="hover:text-white/70" data-id="element-12099" data-mp="piyo8e7odgh6">
                  Terms
                </button>
                <button type="button" className="hover:text-white/70" data-id="element-12100" data-mp="uhi9sdicwq90">
                  Privacy
                </button>
              </div>
            </div>
          </aside>

          {/* ── Right: white payment form ─────────────────────────────────── */}
          <div className="px-5 py-8 sm:px-10 lg:py-16 lg:pl-16 lg:pr-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))]" data-id="element-12101" data-mp="2oqf1jgd5i6jw">
            <div className="mx-auto w-full max-w-md" data-id="element-12102" data-mp="2jz6psdocm93k">
              {/* Express checkout — wallets above the card form */}
              <div className="grid grid-cols-2 gap-3" data-id="element-12103" data-mp="2f1narbjttsak">
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Apple Pay" className="flex h-11 items-center justify-center gap-1 rounded-lg bg-black text-white transition-opacity hover:opacity-85" data-id="element-12104" data-mp="2a1iy5wm1hhpm">
                  
                  <svg viewBox="0 0 384 512" className="h-[17px] w-[17px] fill-current" aria-hidden="true" data-id="element-12105" data-mp="32rhp5xjsnowx">
                    
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-12106" data-mp="2xt6exlpl7ce6" />
                  </svg>
                  <span className="text-[16px] font-medium" data-id="element-12107" data-mp="1opto3983tx2t">Pay</span>
                </button>
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Google Pay" className="flex h-11 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white text-[#3c4043] transition-colors hover:bg-neutral-50" data-id="element-12108" data-mp="19t2q94e2wtfb">
                  
                  <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google.svg" alt="" className="h-[18px] w-[18px]" data-id="element-12109" data-mp="15sirxxhrrjrl" />
                  
                  <span className="text-[15px] font-medium" data-id="element-12110" data-mp="w1o3qnkyqa3i">Pay</span>
                </button>
              </div>

              <div className="my-6 flex items-center gap-4 text-[14px] text-[#6B7280]" data-id="element-12111" data-mp="i1mvuo6a62ej">
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12112" data-mp="m3i1u5z5p3yn" />
                Or pay with card
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12113" data-mp="3f6645cuuef4" />
              </div>

              <div className="space-y-4" data-id="element-12114" data-mp="3ulqufhbiz88g">
                <div data-id="element-12115" data-mp="3umsh6j1wlj60">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12116" data-mp="3unbshgx1t9b2">
                    Email
                  </label>
                  <input type="email" placeholder="faith@company.com" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12117" data-mp="7h19rgi9d3w2" />
                  
                </div>

                <div data-id="element-12118" data-mp="v5kghrlnt8m1">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12119" data-mp="v6415g2p2b3r">
                    Card information
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12120" data-mp="154js9j1q1kd9">
                    <div className="flex items-center border-b border-neutral-200" data-id="element-12121" data-mp="103vqc2byfj49">
                      <input placeholder="1234 1234 1234 1234" inputMode="numeric" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12122" data-mp="27hwbabbytrsd" />
                      
                      <div className="flex items-center gap-1 pr-3" aria-hidden="true" data-id="element-12123" data-mp="2lhx9fj0uil45">
                        
                        {['visa', 'mastercard', 'amex', 'discover'].map(brand => <img key={brand} src={`https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons/flat/${brand}.svg`} alt="" className="h-4 w-auto rounded-[2px]" data-id="element-12124" data-mp="1nvzxy5kvvg1p" />)}
                      </div>
                    </div>
                    <div className="flex" data-id="element-12125" data-mp="22j2wmfkxzuqm">
                      <input placeholder="MM / YY" className="w-1/2 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12126" data-mp="22jup3hj8tcco" />
                      
                      <input placeholder="CVC" inputMode="numeric" className="w-1/2 border-l border-neutral-200 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12127" data-mp="1a1qi17qs01kc" />
                      
                    </div>
                  </div>
                </div>

                <div data-id="element-12128" data-mp="sjrzqb8cpsr6">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12129" data-mp="sj8i6vmm56do">
                    Name on card
                  </label>
                  <input placeholder="Faith Anderson" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12130" data-mp="jk4zhsrcyszp" />
                  
                </div>

                <div data-id="element-12131" data-mp="4wa587xpcgki">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12132" data-mp="4x20fhne0ca9">
                    Country or region
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12133" data-mp="3s06uu51dhjul">
                    <div className="relative border-b border-neutral-200" data-id="element-12134" data-mp="2guns87e6knh1">
                      <select defaultValue="United States" className="w-full appearance-none bg-transparent px-3 py-2.5 pr-9 text-[15px] text-[#30313D] outline-none" data-id="element-12135" data-mp="2by6uclh6iuqr">
                        
                        <option data-id="element-12136" data-mp="22w7g20xju4ri">United States</option>
                        <option data-id="element-12137" data-mp="1xxmdhuutr8zw">Canada</option>
                        <option data-id="element-12138" data-mp="1xzgz7bdx7551">United Kingdom</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12139" data-mp="1t64jxrv4saoy" />
                      
                    </div>
                    <input placeholder="ZIP" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12140" data-mp="1k7ajfqeoj064" />
                    
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(finalStep)} className="mt-6 w-full rounded-lg bg-[#EA580C] py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#d24e08]" data-id="element-12141" data-mp="1aaej47yxpkcn">
                
                Subscribe
              </button>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#8792A2]" data-id="element-12142" data-mp="3vtl3ioic6bqv">
                By confirming your subscription, you allow Giga to charge your
                card for this payment and future payments in accordance with
                their terms. Your 7-day trial is free — you won't be charged
                until {futureDate}, and you can cancel anytime.
              </p>

              {/* mobile-only Stripe footer */}
              <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-neutral-400 lg:hidden" data-id="element-12143" data-mp="3c40vhga2p7jw">
                <span className="flex items-center gap-1.5" data-id="element-12144" data-mp="2keo2shcshy3o">
                  Powered by{' '}
                  <span className="font-bold text-neutral-500" data-id="element-12145" data-mp="2og9ieagpkqhk">stripe</span>
                </span>
                <span className="h-3 w-px bg-neutral-200" data-id="element-12146" data-mp="3rr6v8e2670ff" />
                <button type="button" className="hover:text-neutral-600" data-id="element-12147" data-mp="3rpm30v3bvtkq">
                  Terms
                </button>
                <button type="button" className="hover:text-neutral-600" data-id="element-12148" data-mp="3mpzlwakdpt6n">
                  Privacy
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 1: Landing
  const LANDING_TESTIMONIALS = [{
    quote: "Nothing short of revolutionary. The best attempt I've ever had as a CEO to turn every employee into an AI-first operator.",
    name: 'Tony Ennis',
    title: 'CEO, Brightwave',
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_14.png"
  }, {
    quote: "Giga has completely transformed how our engineering team accesses context. It's like having a senior engineer who knows everything.",
    name: 'Sarah Chen',
    title: 'VP Engineering, Northwind',
    image: 'https://i.pravatar.cc/150?u=sarah'
  }, {
    quote: 'The seamless integration with Slack means my team actually uses it. No new tabs, just instant answers where we already work.',
    name: 'Marcus Lee',
    title: 'Product Lead, Acme Corp',
    image: 'https://i.pravatar.cc/150?u=marcus'
  }];
  return <div className="min-h-screen flex font-body bg-white" data-id="element-12149" data-mp="vktcpsaux85o">
      {/* Teams isn't live — the hover state on that button opens this. */}
      <AnimatePresence data-id="element-12150" data-mp="1jbz2az6lnet6">
        {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => setShowWaitlist(false)} data-id="element-12151" data-mp="1oa0jfekgt420" />}
      </AnimatePresence>

      {/* Left Pane - Product Scenery */}
      <div className="hidden lg:flex w-1/2 bg-[#2B211A] border-r border-neutral-800 relative overflow-hidden flex-col p-12" data-id="element-12152" data-mp="1ejyyuwrbm3ru">
        {/* Shared dark-green grid + subtle glow */}
        <DarkGreenTexture glow="top-left" data-id="element-12153" data-mp="270rssg4gcuge" />

        <Link to="/" aria-label="Giga home" className="relative z-10 flex items-center gap-2 mb-20 md:mb-24" data-id="element-12154" data-mp="271jlnj6o0e2z">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-12155" data-mp="jdrfjwlwzbk6" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12156" data-mp="4fflzytu8ix8" />
          
        </Link>

        {/* Product story — auto-cycling feature showcase (Integrations,
         Smart Memory, Autonomous work) */}
        <div className="relative z-10 flex flex-col items-center w-full" data-id="element-12157" data-mp="ne1o9h13cg2q">
          <SignupShowcase compactCounter counterPosition="bottom" headlinePosition="bottom" data-id="element-12158" data-mp="ndrz1vnvecy3" />
          
          {false && <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: 0.1
            }
          }
        }} className="w-full max-w-sm flex flex-col items-center gap-6" data-id="element-12159" data-mp="1kva6uo3yg0ok">
            
              {/* Speed chip */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: -8
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-1.5 text-[#2B211A] text-xs font-bold shadow-lg" data-id="element-12160" data-mp="1xb0192od5xmh">
              
                <motion.span animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-[#2B211A]" data-id="element-12161" data-mp="2g2j499ie1boi" />
              
                Live in under 60 seconds
              </motion.div>

              {/* Setup checklist — ticks off fast */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: 12
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="w-full bg-white border border-neutral-200 rounded-2xl p-5 shadow-xl shadow-black/5 space-y-3" data-id="element-12162" data-mp="25bnxhdlicn2r">
              
                {[{
              label: 'Connected to Slack',
              activeLabel: 'Connecting to Slack…',
              icon: 'slack'
            }, {
              label: 'Synced your tools',
              activeLabel: 'Syncing your tools…',
              tools: true
            }, {
              label: 'Giga is live',
              activeLabel: 'Booting Giga…',
              giga: true
            }].map((item, i) => {
              const isDone = setupStep > i;
              const isActive = setupStep === i;
              return <motion.div key={item.label} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                show: {
                  opacity: 1,
                  x: 0
                }
              }} className={`relative flex items-center justify-between gap-3 rounded-xl px-2 py-1 transition-colors ${item.giga && isDone ? 'bg-[#F97316]/10' : ''}`} data-id="element-12163" data-mp="13mu6wjf7s414" data-mpi={i}>
                    
                      {/* Lime glow payoff on the final 'Giga is live' row */}
                      {item.giga && isDone && <motion.span aria-hidden="true" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 1, 0.5]
                }} transition={{
                  duration: 0.8
                }} className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-[#F97316]/50" data-id="element-12164" data-mp="2rkbgmg1i557q" />}
                      <div className="relative flex items-center gap-3" data-id="element-12165" data-mp="2mlrtwdf33peb">
                        <div className="w-6 h-6 shrink-0 flex items-center justify-center" data-id="element-12166" data-mp="fu0qawxenjww">
                          <AnimatePresence mode="wait" data-id="element-12167" data-mp="ku5cn3kzv8v1">
                            {isDone ? <motion.div key="done" initial={{
                        scale: 0
                      }} animate={{
                        scale: 1
                      }} transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 18
                      }} className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center" data-id="element-12168" data-mp="kr9djbxeue50">
                            
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12169" data-mp="2l2q6ufrzum6o">
                              
                                  <path d="M20 6L9 17l-5-5" data-id="element-12170" data-mp="2c1s2txhk2eo3" />
                                </svg>
                              </motion.div> : isActive ? <motion.div key="active" className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#EA580C] animate-spin" data-id="element-12171" data-mp="273qo1or5x1if" /> : <motion.div key="pending" className="w-5 h-5 rounded-full border-2 border-neutral-200" data-id="element-12172" data-mp="5aj7yd3q6fvh" />}
                          </AnimatePresence>
                        </div>
                        <span className={`font-medium text-sm transition-colors ${isDone ? 'text-neutral-700' : isActive ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-12173" data-mp="cs7lmde6kpl3">
                        
                          {isActive ? item.activeLabel : item.label}
                        </span>
                      </div>

                      {/* Right-side badges fade in once the row completes */}
                      <AnimatePresence data-id="element-12174" data-mp="3q2wuxulkosgb">
                        {isDone && item.icon === 'slack' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-12175" data-mp="3uzx268u5s190">
                        
                            <SlackIcon size={16} data-id="element-12176" data-mp="1j9cnf6ko0gul" />
                          </motion.div>}

                        {isDone && item.tools && <div className="flex -space-x-1.5" data-id="element-12177" data-mp="2acyptfurd0fi">
                            {['notion', 'linear', 'google-drive', 'figma'].map((slug, j) => <motion.div key={slug} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: j * 0.06
                    }} className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden" data-id="element-12178" data-mp="2fa8id4dg1s7k" data-mpi={j}>
                            
                                  <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={slug} className="w-4 h-4 object-contain" data-id="element-12179" data-mp="1hot1kj4hwtax" />
                            
                                </motion.div>)}
                          </div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </motion.div>
            </motion.div>}
        </div>

        {/* Social proof — anchored to the bottom of the brand pane so the
         form column stays focused on the CTAs and workspace list. */}
        <div className="relative z-10 mt-auto flex items-center gap-4 pt-10" data-id="element-12180" data-mp="1f3bu8cqclx3s">
          <div className="flex -space-x-2.5" data-id="element-12181" data-mp="1ac3l7hsxkdr5">
            {["https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_26.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_25.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_24.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_23.png"].map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-8 h-8 rounded-full object-cover border-2 border-[#2B211A] shadow-sm relative" style={{
            zIndex: 4 - i
          }} data-id="element-12182" data-mp="3uv9e1mv41go2" data-mpi={i} />)}
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-12183" data-mp="23q66uvh5x5r3">
            <div className="flex items-center gap-0.5" aria-hidden="true" data-id="element-12184" data-mp="27r8arpm0y5ze">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" data-id="element-12185" data-mp="2cqumtvq49g6m" />)}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/60" data-id="element-12186" data-mp="2hqh0255y2wff">
              Loved by early adopters
            </span>
          </div>
        </div>
      </div>

      {/* Right Pane - Signup Form */}
      <div className="w-full lg:w-1/2 max-lg:min-h-screen flex flex-col items-center justify-start pt-0 lg:p-12 relative bg-[#FAF8F4] overflow-hidden" data-id="element-12187" data-mp="2rg0mfksv98f1">
        {/* Mobile-only brand texture — a square-dot halftone field that fades in
         at the top and bottom edges and clears through the middle so the form
         stays clean. Hidden on desktop, where the left pane carries the brand. */}
        <div aria-hidden="true" className="lg:hidden dot-matrix z-0" style={{
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)',
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)'
      }} data-id="element-12188" data-mp="3fkrsrg3qu23h" />
        

        {/* Mobile-only brand lockup — pinned to the very top-left of the screen.
         On desktop (lg+) the left pane already shows the Giga logo. */}
        <Link to="/" aria-label="Giga home" className="flex lg:hidden items-center gap-2 absolute top-6 left-6 z-20" data-id="element-12189" data-mp="3tabo40uzvx2r">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" data-id="element-12190" data-mp="3oibtmnxp1wdk" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-12191" data-mp="g4381ztla5uw" />
          
        </Link>

        {/* Small top spacer on desktop — the pane now carries the workspaces
         list too, so the content block starts higher to fit evenly. */}
        <div className="hidden lg:block h-4 mb-4" aria-hidden="true" data-id="element-12192" data-mp="3nsufxcs3lbqo" />

        <div className="relative z-10 w-full max-w-[400px] flex flex-col flex-1 lg:flex-none justify-center lg:justify-start px-6 md:px-8 lg:px-0 pt-16 lg:pt-0 pb-5 lg:pb-0" data-id="element-12193" data-mp="3rsv8e1c17ngq">
          {/* The eyebrow and the separate offer badge are gone — three stacked
           labels above the headline was the source of the crowding. The offer
           now lives in the headline itself, where it gets real weight, with
           the amount in orange so it still reads at a glance. */}
          <h1 className="font-title font-medium text-2xl md:text-4xl tracking-tight text-[#2B211A] mb-3 text-center lg:text-left" data-id="element-12194" data-mp="12acqr5zf73el">
            {isLogin ? <>
                Your entire work stack,
                <br data-id="element-12195" data-mp="2rrhqruzbo7xr" />
                one chat away.
              </> : <>
                Try Giga free, with
                <br data-id="element-12196" data-mp="2mson0u9u7vas" />
                <span className="text-[#EA580C]" data-id="element-12197" data-mp="2mrdj8uge6njo">$100 in credits</span> included.
              </>}
          </h1>

          {/* Short product line rather than the old three-clause sentence.
           The headline now carries the offer, so this only has to say what
           Giga is — and the task feed beside it already demonstrates the
           "connect, ask, act" story better than a sentence can. */}
          <p className="text-[15px] md:text-base text-neutral-500 leading-relaxed mb-6 text-center lg:text-left" data-id="element-12198" data-mp="23wou9h91cnq5">
            Every tool your company runs on, one brain.
          </p>

          {/* Mobile-only notification illustration. The avatar/star social
           proof that used to sit here was removed — on a screen whose only job
           is "pick an auth method", it was a third competing block above the
           fold. Desktop still carries it in the left brand pane. */}
          <div className="lg:hidden w-full flex justify-center mb-5" data-id="element-12199" data-mp="5hewhydh1gq">
            <SignupShowcase variant="light" counterPosition="bottom" compactCounter data-id="element-12200" data-mp="53sjad5wejrk" />
          </div>

          <div className="flex flex-col gap-3" data-id="element-12201" data-mp="a589ejmxnggt">
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('slack');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl bg-[#2B211A] text-white font-bold transition-shadow hover:shadow-lg hover:shadow-black/5" data-id="element-12202" data-mp="f2zyl5w70ohb">
              
              <SlackIcon size={20} data-id="element-12203" data-mp="2zs7ay4jn4ztn" />
              Continue with Slack
            </motion.button>
            {/* Google is a full second front door, not a fallback — plenty of
             teams have no Slack at all. It sits directly under Slack (equal
             weight, lighter fill) so neither reads as the only way in. */}
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('google');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(20);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl border border-[#EAE4D9] bg-white font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-12204" data-mp="17vlfv02uxegr">
              <GoogleIcon size={20} data-id="element-12205" data-mp="aaz2p6xkmoy1" />
              Continue with Google
            </motion.button>
            {/* Teams isn't live. Rather than a dead disabled row, hovering
             turns it into the one thing you can actually do: join the list. */}
            <button type="button" onClick={() => setShowWaitlist(true)} className="group relative flex w-full items-center justify-center gap-3 rounded-xl border border-[#EFEAE1] bg-white/60 px-4 py-3 font-bold text-neutral-400 transition-all hover:border-[#D4D0C8] hover:bg-white hover:text-[#2B211A] md:py-3.5" data-id="element-12206" data-mp="1p5xo2gk3vzvl">
              <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" alt="" aria-hidden="true" className="h-5 w-5 opacity-40 transition-opacity group-hover:opacity-100" data-id="element-12207" data-mp="1k6uj0w44ltnm" />
              <span className="group-hover:hidden" data-id="element-12208" data-mp="1bdhk9djdrxtm">Continue with Teams</span>
              <span className="hidden group-hover:inline" data-id="element-12209" data-mp="wha0gebvwyev">Join the waitlist</span>
              <span className="absolute right-4 rounded-full bg-[#F0EBE2] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500 transition-colors group-hover:bg-[#FFEDD5] group-hover:text-[#B4470F]" data-id="element-12210" data-mp="wjcoisth8hgy">
                Soon
              </span>
            </button>
          </div>

          {/* Book a demo — a real third option for buyers who want a walkthrough
           before connecting a workspace, but outlined and set apart by an "or"
           rule so the two auth buttons stay the primary path. */}
          {!isLogin && <>
              <div className="mt-4 flex items-center gap-4" aria-hidden="true" data-id="element-12211" data-mp="10rujlrf4vuoo">
                <div className="h-px flex-1 bg-neutral-200" data-id="element-12212" data-mp="wsdc6sg5cngv" />
                <span className="text-[13px] text-neutral-400" data-id="element-12213" data-mp="1aiz04vkgjnlm">or</span>
                <div className="h-px flex-1 bg-neutral-200" data-id="element-12214" data-mp="1ai727e3e1ds6" />
              </div>
              <a href="https://calendly.com/tonyennis/ai-adoption-for-your-business-clone" target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center rounded-xl border border-[#EAE4D9] bg-white px-4 py-3 font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:bg-neutral-50" data-id="element-12215" data-mp="15l6wj4agad1l">
                Book a demo
              </a>
              <p className="mt-4 text-center text-[13px] text-neutral-400" data-id="element-12216" data-mp="1i9anb55vo0g0">
                No credit card required
              </p>
            </>}

          {/* Your Workspaces — returning users with remembered sessions can
           jump straight into a workspace instead of re-authing. Only the most
           recent one shows by default: three near-identical rows made the
           screen feel busy, and the other two are one click away. */}
          {!signedOut && <div className="w-full mt-5 md:mt-6" data-id="element-12217" data-mp="21yuru3exmmi8">
              <div className="flex items-center gap-4 mb-3" data-id="element-12218" data-mp="2fv88xbfk0lw6">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12219" data-mp="2axg9zxghrzih" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-12220" data-mp="2axzqd9qhufue">
                  Continue as
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12221" data-mp="gjp67ekug9uy" />
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden" data-id="element-12222" data-mp="2s6slcge3kmi0">
                {[{
              initials: 'T',
              name: 'Tonic',
              email: 'faith@tonichq.com'
            }, ...(showAllWorkspaces ? [{
              initials: 'B',
              name: 'Brightwave',
              email: 'sarah.chen@brightwave.io'
            }, {
              initials: 'N',
              name: 'Northwind Labs',
              email: 'marcus.lee@northwindlabs.com'
            }] : [])].map(ws => <button key={ws.name} onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-[#FFFCF8] transition-colors group text-left" data-id="element-12223" data-mp="2egp5q89q3r1j">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-bold text-[13px]" data-id="element-12224" data-mp="24hgbrfeuycf6">
                      {ws.initials}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col" data-id="element-12225" data-mp="2os6l0inlewr">
                      <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-12226" data-mp="19foua7mgq9f">
                        {ws.name}
                      </span>
                      <span className="text-[13px] text-neutral-500 truncate" data-id="element-12227" data-mp="3sh20ap0l1x0u">
                        {ws.email}
                      </span>
                    </div>
                    <div className="flex w-6 items-center justify-end shrink-0" data-id="element-12228" data-mp="3jflqojhldyxo">
                      <ArrowRight size={16} className="text-neutral-300 group-hover:text-[#EA580C] group-hover:translate-x-0.5 transition-all" data-id="element-12229" data-mp="jxujr8l28ofi" />
                    </div>
                  </button>)}
              </div>

              <div className="mt-3 flex items-center justify-center gap-2 text-[13px] text-neutral-400" data-id="element-12230" data-mp="a7jdvghtg55z">
                {!showAllWorkspaces && <>
                    <button onClick={() => setShowAllWorkspaces(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-12231" data-mp="12p58pk786r4r">
                      Use another workspace
                    </button>
                    <span aria-hidden="true" data-id="element-12232" data-mp="2glon35rftp3z">·</span>
                  </>}
                <button onClick={() => setSignedOut(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-12233" data-mp="2gnhs6gmek19v">
                  Sign out
                </button>
              </div>
            </div>}

          {/* Returning users — quiet sign-in link, but ONLY when there are no
           remembered workspaces on this device (the Your Workspaces list IS
           the signed-in path, so showing both would be redundant). Hidden on
           /login, which already is the sign-in. */}
          {!isLogin && signedOut && <p className="mt-8 text-center text-sm text-neutral-500" data-id="element-12234" data-mp="39a2kpkem6jf0">
              Already have an account?{' '}
              <Link to="/app-screen-mockups/sign-in" className="font-bold text-[#2B211A] underline underline-offset-2 hover:text-[#EA580C] transition-colors" data-id="element-12235" data-mp="3dbfpdr5z0gkc">
              
                Sign in
              </Link>
            </p>}

          <p className="text-center text-[11px] text-neutral-400 mt-3 pt-0 mb-0 leading-relaxed max-w-[320px] mx-auto" data-id="element-12236" data-mp="3nan4ojw9mla3">
            By continuing, you agree to Giga's{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12237" data-mp="34olaikdw3kkn">
              
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12238" data-mp="3edv0nup6at64">
              
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>;
}